/**
 * src/db/index.js — Sentavus Schedule Data Layer
 *
 * Schedule shape (multi-slot):
 *   agent.schedule = {
 *     Mon: [{start:'09:00', end:'13:00', location:'office'}, {start:'14:00', end:'18:00', location:'home'}],
 *     Tue: [{start:'09:00', end:'18:00', location:'office'}],
 *     Wed: [],   // day off
 *     ...
 *   }
 */

import { createClient } from '@supabase/supabase-js'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || ''
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || ''
export const USE_SUPABASE = Boolean(SUPABASE_URL && SUPABASE_KEY)
export const MODE = USE_SUPABASE ? 'supabase' : 'local'

export const supabase = USE_SUPABASE ? createClient(SUPABASE_URL, SUPABASE_KEY) : null

// ─── Access token for api.sentavus.com (x-viewer-token) ───────────────────────
// 🚨 THE ONE PLACE the session token is read. Do NOT call supabase.auth.getSession()
// per request again — that is the bug this replaces (2026-07-26).
//
// Symptom: a modal spun forever — no network request, no error, no timeout. Server was
// provably fine (every endpoint it needed answered in <0.7s). Admin sessions left open
// for hours were the ones that broke.
//
// Root cause: an old comment claimed "getSession() is a local read (no network)". That
// is only true while the token is VALID. On an expired token gotrue-js refreshes over
// the network, serialized behind a Web Lock — and EVERY core call awaited it first
// (`coreApi` line 1). One stalled refresh means every later getSession() queues behind
// the same lock: no fetch is ever issued, nothing rejects, so a `.catch()` cannot help
// and `setLoading(false)` never runs. The Desk fires dozens of core calls per screen,
// all contending on that lock.
//
// Two defences, both required:
//  1. CACHE — one token reused for TOKEN_TTL, refreshed by the auth listener below, so
//     normal traffic never touches the lock at all.
//  2. DEADLINE — never await the session forever. On timeout fall back to the last known
//     good token: the request then goes out and either works or 401s, which the UI can
//     SHOW. A visible error beats an eternal spinner.
const TOKEN_TTL_MS = 30_000
let cachedToken = null
let cachedAt = 0

// The listener is the primary source of truth — it fires on refresh, sign-in and
// sign-out, so the cache is normally warm and correct without anyone asking for it.
supabase?.auth.onAuthStateChange((event, session) => {
  cachedToken = session?.access_token || null
  cachedAt = cachedToken ? Date.now() : 0
  if (event === 'SIGNED_OUT') { cachedToken = null; cachedAt = 0 }
})

export async function getAccessToken() {
  if (cachedToken && Date.now() - cachedAt < TOKEN_TTL_MS) return cachedToken
  try {
    const res = await Promise.race([
      supabase?.auth.getSession(),
      // ⚠️ Deliberately resolves (not rejects) with a sentinel: a rejection here would
      // turn a slow refresh into a failed request, and the cached token is usually still
      // valid. 3s is far longer than a healthy local read (~1ms).
      new Promise(resolve => setTimeout(() => resolve('__timeout__'), 3000)),
    ])
    if (res === '__timeout__') {
      console.warn('[auth] getSession() exceeded 3s — using the last known token')
      return cachedToken
    }
    const token = res?.data?.session?.access_token || null
    if (token) { cachedToken = token; cachedAt = Date.now() }
    return token
  } catch { return cachedToken }
}

// ─── Current user's role (app_users) ──────────────────────────────────────────
// Role gates the UI (see lib/permissions.js). Keyed by auth uid. Least-privilege by
// default: an unknown/unseeded logged-in user is treated as 'agent' (Desk only) —
// 'manager' was the floor before the agent role got real access (2026-07-05); now a
// missing app_users row must not grant company-wide tabs. Local mode → full access.
//
// role_assigned_at (2026-07-09): DB-trigger-maintained timestamp of the last role change —
// used by AgentsTab so manager/agent_hr only see resigned agents whose resign_date falls
// on/after their OWN promotion, not the company's whole resignation history.
// ⚠️ This column ships in a migration (app-users-schema.sql) that must be run BY HAND in the
// SQL editor — it does NOT auto-deploy with this code. If it hasn't run yet, selecting it
// 400s and would otherwise collapse EVERY logged-in user (including admins) to the 'agent'
// floor. So: try the extended select first, and if that specific column is missing, fall
// back to the plain `role`-only select instead of failing closed for the whole company.
export async function getMyRoleInfo() {
  if (!USE_SUPABASE) return { role: 'admin', roleAssignedAt: null }
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return { role: 'agent', roleAssignedAt: null }
  const { data, error } = await supabase
    .from('app_users').select('role,role_assigned_at').eq('auth_user_id', user.id).maybeSingle()
  if (!error) return { role: data?.role || 'agent', roleAssignedAt: data?.role_assigned_at || null }

  console.warn('getMyRoleInfo extended select failed, falling back to role-only:', error.message)
  const { data: fallbackData, error: fallbackError } = await supabase
    .from('app_users').select('role').eq('auth_user_id', user.id).maybeSingle()
  if (fallbackError) { console.warn('getMyRoleInfo fallback also failed:', fallbackError.message); return { role: 'agent', roleAssignedAt: null } }
  return { role: fallbackData?.role || 'agent', roleAssignedAt: null }
}

// app_user_role_history (2026-07-09): append-only log of every role period this user has
// held (started_at/ended_at, ended_at null = current) — see app-users-schema.sql. Unlike
// role_assigned_at (a single overwritten timestamp), this survives demote+repromote: a
// manager demoted to agent and later re-promoted keeps visibility into resignations from
// BOTH manager stints, not just the latest one. Same-shape fallback as getMyRoleInfo: this
// table ships in a migration that may not have run yet, so a failed/empty read just means
// "no history available" (callers fall back to role_assigned_at), never an error.
export async function getMyRoleHistory() {
  if (!USE_SUPABASE) return []
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return []
  const { data, error } = await supabase
    .from('app_user_role_history').select('role,started_at,ended_at')
    .eq('auth_user_id', user.id).order('started_at', { ascending: true })
  if (error) { console.warn('getMyRoleHistory failed (migration may not be applied yet):', error.message); return [] }
  return data || []
}

export async function getMyRole() {
  return (await getMyRoleInfo()).role
}

// PostgREST caps EVERY response at 1000 rows. Any unbounded read must page through
// with .range() or the tail is silently dropped — which made wider Stats ranges show
// SMALLER totals than narrower ones. `buildQuery` must return a FRESH builder each
// call (filters + a STABLE/unique .order applied, no .range) so pages don't overlap
// or skip at boundaries. Order by a unique column (or add a tiebreaker) — ordering by
// a non-unique column alone can duplicate/drop rows across page boundaries.
async function fetchAllRows(buildQuery, pageSize = 1000) {
  const all = []
  for (let offset = 0; ; offset += pageSize) {
    const { data, error } = await buildQuery().range(offset, offset + pageSize - 1)
    if (error) throw error
    if (!data?.length) break
    all.push(...data)
    if (data.length < pageSize) break
  }
  return all
}

// ─── localStorage ─────────────────────────────────────────────────────────────
const LS_AGENTS          = 'sva_agents_v6'   // bumped: array-based schedule
const LS_OFFICES         = 'sva_offices_v1'
const LS_ATTENDANCE      = 'sva_attendance_v1'
const LS_SCHEDULE_HIST   = 'sva_schedule_history_v1'

function lsRead(key)         { try { return JSON.parse(localStorage.getItem(key) || '[]') } catch { return [] } }
function lsWrite(key, value) { localStorage.setItem(key, JSON.stringify(value)) }

// ─── Offices ──────────────────────────────────────────────────────────────────
export async function getOffices() {
  if (!USE_SUPABASE) {
    const offices = lsRead(LS_OFFICES)
    if (offices.length === 0) {
      const seed = [{ id: '1', name: 'Main Office', location: 'Tbilisi', capacity: 8 }]
      lsWrite(LS_OFFICES, seed)
      return seed
    }
    return offices
  }
  const { data, error } = await supabase.from('offices').select('*').order('name')
  if (error) throw error
  return data
}

export async function addOffice(data) {
  if (!USE_SUPABASE) {
    const item = { ...data, id: crypto.randomUUID() }
    lsWrite(LS_OFFICES, [...lsRead(LS_OFFICES), item])
    return item
  }
  const { data: row, error } = await supabase.from('offices').insert(data).select().single()
  if (error) throw error
  return row
}

export async function deleteOffice(id) {
  if (!USE_SUPABASE) {
    lsWrite(LS_OFFICES, lsRead(LS_OFFICES).filter(o => o.id !== id))
    const agents = lsRead(LS_AGENTS).map(a => a.office_id === id ? { ...a, office_id: null } : a)
    lsWrite(LS_AGENTS, agents)
    return
  }
  const { error } = await supabase.from('offices').delete().eq('id', id)
  if (error) throw error
}

// 0008/0009 — the office Wi-Fi check, for the admin office modal.
// Status distinguishes THREE states and the panel must keep them apart: never set up, set up and
// gone quiet, and working-but-empty. Collapsing any two teaches an admin to ignore the line.
export async function getOfficePresenceStatus(officeId) {
  if (!USE_SUPABASE || !officeId) return null
  const { data, error } = await supabase.rpc('office_presence_status', { p_office_id: officeId })
  if (error) throw error
  return data || null
}
// The token is ISSUED, never typed: the client asks and is handed the result, so a weak or reused
// secret cannot arrive from a form. Rotating invalidates the old one immediately — the reporter
// then fails loudly on its next post instead of going quiet, which is the failure we can see.
export async function rotateOfficePresenceToken(officeId) {
  if (!USE_SUPABASE || !officeId) return null
  const { data, error } = await supabase.rpc('office_presence_rotate_token', { p_office_id: officeId })
  if (error) throw error
  return data || null
}

// 0034 — "Connect to router" (owner 2026-09-22). The extension reads the router; these link the
// office in the database with the viewer's own session. Admin, or the office's own manager — the
// database decides (office_reporter_can_manage), and says so in office_presence_status.can_manage.
export async function connectOfficeRouter(officeId, { router_mac, ssid, model } = {}) {
  if (!USE_SUPABASE) throw new Error('offline')
  const { data, error } = await supabase.rpc('office_reporter_connect',
    { p_office_id: officeId, p_router_mac: router_mac, p_ssid: ssid || null, p_model: model || null })
  if (error) throw new Error(error.message || 'could not connect the router')
  return data
}
// 0043 — the router reports by itself (owner 2026-10-29). Issues the router's own token; the Desk turns it into the
// one-time install command. Replaces a computer's token for the same office.
export async function getRouterSelfToken(officeId) {
  if (!USE_SUPABASE) throw new Error('offline')
  const { data, error } = await supabase.rpc('office_router_self_token', { p_office_id: officeId })
  if (error) throw new Error(error.message || 'could not issue the router key')
  return data
}
// The reporter found a different router: link the office to it (one click, no trip to the office).
export async function acceptOfficeRouter(officeId) {
  if (!USE_SUPABASE) throw new Error('offline')
  const { data, error } = await supabase.rpc('office_reporter_use_router', { p_office_id: officeId })
  if (error) throw new Error(error.message || 'could not change the router')
  return data
}
export async function disconnectOfficeRouter(officeId) {
  if (!USE_SUPABASE) throw new Error('offline')
  const { error } = await supabase.rpc('office_reporter_disconnect', { p_office_id: officeId })
  if (error) throw new Error(error.message || 'could not disconnect the router')
}

// 0011 — the agent's own side: which phone the office should recognise as theirs.
// ⚠️ These deliberately do NOT swallow the RPC's error. Its refusals are written to be read by
// the agent standing there ("the office cannot see that device right now — connect the phone to
// the office Wi-Fi and check the address"), and a generic "could not save" would throw away the
// only thing that tells them which of the two possible mistakes they made.
export async function getMyOfficeDevices(agentId) {
  if (!USE_SUPABASE || !agentId) return []
  const { data, error } = await supabase.rpc('office_devices_for_agent', { p_agent_id: agentId })
  if (error) throw error
  return data || []
}
export async function registerMyOfficeDevice(agentId, mac, label) {
  if (!USE_SUPABASE) throw new Error('offline')
  const { data, error } = await supabase.rpc('office_device_register',
    { p_agent_id: agentId, p_mac: mac, p_label: label || null })
  if (error) throw new Error(error.message || 'could not register the device')
  return data || null
}
export async function revokeMyOfficeDevice(deviceId, actorId) {
  if (!USE_SUPABASE) throw new Error('offline')
  const { data, error } = await supabase.rpc('office_device_revoke',
    { p_device_id: deviceId, p_actor: actorId || null })
  if (error) throw new Error(error.message || 'could not remove the device')
  return data || null
}

export async function updateOffice(id, updates) {
  if (!USE_SUPABASE) {
    const offices = lsRead(LS_OFFICES).map(o => o.id === id ? { ...o, ...updates } : o)
    lsWrite(LS_OFFICES, offices)
    return offices.find(o => o.id === id)
  }
  const { data, error } = await supabase.from('offices').update(updates).eq('id', id).select().single()
  if (error) throw error
  return data
}

// app_users is the SEPARATE scheduler-login/permission table (admin/manager UI access,
// keyed to Supabase Auth) — distinct from the agents roster. Lets AgentModal offer linking
// an agent row to its matching login (agents.auth_user_id), so the two identities aren't
// only reconciled by someone noticing a name match (see 2026-07-02 Tako/Gigi split-identity
// discussion). RLS: authenticated may read (rls policy app_users_read), never write from here.
export async function getAppUsers() {
  if (!USE_SUPABASE) return []
  const { data, error } = await supabase.from('app_users').select('auth_user_id,email,role')
  if (error) throw error
  return data
}

// ─── Agents ───────────────────────────────────────────────────────────────────
export async function getAgents() {
  if (!USE_SUPABASE) return lsRead(LS_AGENTS)

  const { data: agents, error: e1 } = await supabase.from('agents').select('*').order('name')
  if (e1) throw e1
  const { data: schedules, error: e2 } = await supabase.from('agent_schedule').select('*')
  if (e2) throw e2

  // Rebuild schedule as arrays: { Mon: [{...}, {...}], ... }
  return agents.map(agent => {
    const schedule = {}
    const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']
    DAYS.forEach(d => { schedule[d] = [] })
    schedules
      .filter(s => s.agent_id === agent.id)
      .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
      .forEach(r => {
        if (schedule[r.day_of_week]) {
          schedule[r.day_of_week].push({
            start:    r.start_time,
            end:      r.end_time,
            location: r.location,
          })
        }
      })
    return { ...agent, schedule }
  })
}

// Today as a LOCAL yyyy-mm-dd. Inlined rather than imported from lib/attendance.js:
// lib/agentLog.js already imports the client from this module, and pulling a lib back
// in here closes the cycle. toISOString() would be wrong — it is UTC, and Georgia is
// UTC+4, so between 00:00 and 04:00 local every stamp would land on yesterday.
function todayLocalStr() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// ⚠️ hire_date IS STAMPED HERE, not in the form (owner ask, 2026-08-29). Before this,
// the ONLY writer was the candidate→agent hire flow (useCandidates.promoteToAgent); an
// agent added directly through "+ Add agent" got hire_date NULL, and a null hire_date is
// not cosmetic — it silently disables the "not hired yet" guard in ListingsTab, the stats
// window clamp in StatsTab, onboarding detection in AttendanceTab, and both newest/oldest
// sorts. 9 of the 15 active agents were in that state when this was found. Stamping in the
// data layer covers BOTH creation paths at once, which a default in the form could not.
// It is a default, not a lock: the field stays editable for a back-dated hire.
// The DATED roster history (scheduler migration 0001), which nothing read until 2026-08-29.
// agents.office_id is only the CURRENT pointer; asking it who was where last month gives
// today's answer to a question about the past, which is how a manager's team figures silently
// rewrite themselves after a transfer. Dates are returned as plain days to compare directly
// against a deal's close date.
// ⚠️ RATES ARE THE THIRD SPELL TABLE (scheduler 0025, 2026-09-20) and they are the one that
// decides MONEY. Office membership and manager coverage have been dated since 0001/0007, but
// `agents.commission` / `.commission_manager` stayed single overwritable numbers, so every
// as-of calculation resolved WHO correctly and then paid them TODAY's percentage. Owner ruling
// 2026-09-20: anything that happens is date-stamped and has an active period — a rate applies
// only after its effective date and must never re-price a deal that closed before it.
// Shaped exactly like the other two (joined/left → started/ended, plain days) so the three can
// be asked the same question by the same kind of function.
export async function getOfficeHistory() {
  if (!USE_SUPABASE) return { memberships: [], coverage: [], rates: [], hours: [] }
  const [mem, cov, rate, hours] = await Promise.all([
    supabase.from('agent_office_membership').select('agent_id, office_id, joined_at, left_at'),
    supabase.from('office_manager').select('agent_id, office_id, joined_at, left_at'),
    supabase.from('agent_rate').select('agent_id, kind, pct, started_at, ended_at'),
    // Dated office hours (scheduler 0026) — what the working day WAS, so editing an office's
    // hours cannot re-measure days already credited.
    supabase.from('office_hours').select('office_id, work_start, work_end, weekend_work_start, weekend_work_end, started_at, ended_at'),
  ])
  const day = r => ({ ...r, joined_at: (r.joined_at || '').slice(0, 10), left_at: r.left_at ? r.left_at.slice(0, 10) : null })
  const rateDay = r => ({
    agent_id: r.agent_id, kind: r.kind, pct: r.pct,
    started_at: (r.started_at || '').slice(0, 10),
    ended_at: r.ended_at ? r.ended_at.slice(0, 10) : null,
  })
  const hourDay = h => ({
    office_id: h.office_id,
    work_start: h.work_start, work_end: h.work_end,
    weekend_work_start: h.weekend_work_start, weekend_work_end: h.weekend_work_end,
    started_at: (h.started_at || '').slice(0, 10),
    ended_at: h.ended_at ? h.ended_at.slice(0, 10) : null,
  })
  return {
    memberships: (mem.data || []).map(day),
    coverage: (cov.data || []).map(day),
    hours: (hours.data || []).map(hourDay),
    // A missing table reads as [] rather than throwing: the ledger then falls back to the
    // roster's current rate exactly as it did before 0025, which is wrong-but-unchanged
    // instead of a blank Stats tab.
    rates: (rate.data || []).map(rateDay),
  }
}

// Recruiting + roster activity, attributable per agent (0006). Only rows carrying
// by_agent_id are returned: a row without it is not attributable, and counting it against
// somebody because their typed label looked similar is exactly the guess 0006 removes.
export async function getRecruitingActivity({ from, to } = {}) {
  if (!USE_SUPABASE) return { candidate: [], agent: [] }
  // ⚠️ ASK FOR THE ROWS YOU COUNT, NOT FOR THE TABLE. Measured 2026-09-05: agent_logs holds
  // 5,652 rows (3,088 with an actor) and this fetch carried no category filter, so PostgREST
  // returned its default first 1,000 and every hire or departure outside that slice was
  // invisible. The Managers ranking showed one manager 2 hires where the data had 5, and
  // another 0 where it had 1 — not a counting bug, a TRUNCATION bug, and it silently affected
  // the "left" column too for as long as that column has existed.
  // The categories below are exactly what ManagersPanel reads; in the same 90-day window they
  // are 71 hired + 21 left, which no page cap will ever reach. An explicit limit is set anyway
  // so a future growth spurt fails loudly against a number someone chose rather than quietly
  // against a default nobody knew was there.
  const TYPES_COUNTED = ['added', 'interviewed', 'hired']
  const CATEGORIES_COUNTED = ['hired', 'left']
  let cq = supabase.from('candidate_logs').select('type, by_agent_id, by_office_id, ts')
    .not('by_agent_id', 'is', null).in('type', TYPES_COUNTED).limit(20000)
  // agent_id is selected because "hired" is counted from THIS table, one row per person hired,
  // and the id is what de-duplicates them (see ManagersPanel).
  let aq = supabase.from('agent_logs').select('agent_id, category, message, by_agent_id, by_office_id, ts')
    .not('by_agent_id', 'is', null).in('category', CATEGORIES_COUNTED).limit(20000)
  if (from) { cq = cq.gte('ts', from); aq = aq.gte('ts', from) }
  if (to)   { cq = cq.lte('ts', to);   aq = aq.lte('ts', to) }
  const [c, a] = await Promise.all([cq, aq])
  return { candidate: c.data || [], agent: a.data || [] }
}

export async function addAgent(data) {
  const { schedule, ...fields } = data
  if (!fields.hire_date) fields.hire_date = todayLocalStr()
  if (!USE_SUPABASE) {
    // Dedup guard: if a candidate_source_id is provided, never create twice
    if (fields.candidate_source_id) {
      const existing = lsRead(LS_AGENTS).find(a => a.candidate_source_id === fields.candidate_source_id)
      if (existing) return existing
    }
    const item = { ...fields, schedule, id: crypto.randomUUID() }
    lsWrite(LS_AGENTS, [...lsRead(LS_AGENTS), item])
    const hireDate = fields.hire_date || new Date().toISOString().slice(0, 10)
    await _saveScheduleHistory(item.id, schedule, hireDate)
    return item
  }
  const { data: agent, error } = await supabase.from('agents').insert(fields).select().single()
  if (error) throw error
  await _replaceSchedule(agent.id, schedule)
  const hireDate = fields.hire_date || new Date().toISOString().slice(0, 10)
  await _saveScheduleHistory(agent.id, schedule, hireDate)
  return { ...agent, schedule }
}

export async function updateAgent(id, updates) {
  const { schedule, ...fields } = updates
  // Schedule changes take effect from today — history lookup finds the most recent
  // entry <= dateStr, so past days with earlier entries remain correct
  const today = new Date().toISOString().slice(0, 10)
  if (!USE_SUPABASE) {
    const agents = lsRead(LS_AGENTS).map(a =>
      a.id === id ? { ...a, ...fields, schedule: schedule ?? a.schedule } : a
    )
    lsWrite(LS_AGENTS, agents)
    if (schedule) {
      await _saveScheduleHistory(id, schedule, today)
    }
    return agents.find(a => a.id === id)
  }
  if (Object.keys(fields).length) {
    const { error } = await supabase.from('agents').update(fields).eq('id', id)
    if (error) throw error
  }
  if (schedule) {
    await _replaceSchedule(id, schedule)
    await _saveScheduleHistory(id, schedule, today)
  }
  const all = await getAgents()
  return all.find(a => a.id === id)
}

export async function deleteAgent(id) {
  if (!USE_SUPABASE) {
    lsWrite(LS_AGENTS, lsRead(LS_AGENTS).filter(a => a.id !== id))
    return
  }
  await supabase.from('agent_schedule').delete().eq('agent_id', id)
  const { error } = await supabase.from('agents').delete().eq('id', id)
  if (error) throw error
}

// ─── Attendance ───────────────────────────────────────────────────────────────
export async function getAttendance({ agentId, dateFrom, dateTo } = {}) {
  if (!USE_SUPABASE) {
    let rows = lsRead(LS_ATTENDANCE)
    if (agentId)  rows = rows.filter(r => r.agent_id === agentId)
    if (dateFrom) rows = rows.filter(r => r.date >= dateFrom)
    if (dateTo)   rows = rows.filter(r => r.date <= dateTo)
    return rows
  }
  // Paginated: the Stats page defaults to a 3-month range and offers 6-month / "all";
  // ~12 agents × that many days exceeds the 1000-row cap, which silently dropped the
  // OLDEST rows (ordered date-desc) and undercounted attendance early in the range.
  return fetchAllRows(() => {
    let q = supabase.from('attendance').select('*')
    if (agentId)  q = q.eq('agent_id', agentId)
    if (dateFrom) q = q.gte('date', dateFrom)
    if (dateTo)   q = q.lte('date', dateTo)
    return q.order('date', { ascending: false }).order('id', { ascending: true })
  })
}

// First recorded check-in per agent, all time — the cohort metrics' "started" date
// (lib/statsModel.cohortMetrics). 620 rows with a check-in on 2026-09-06; reduced here to one
// date per agent so the caller never carries the table.
export async function getFirstCheckins() {
  if (!USE_SUPABASE) {
    const m = new Map()
    for (const r of lsRead(LS_ATTENDANCE)) if (r.check_in && r.date && (!m.has(r.agent_id) || r.date < m.get(r.agent_id))) m.set(r.agent_id, r.date)
    return m
  }
  const rows = await fetchAllRows(() => supabase.from('attendance').select('agent_id, date')
    .not('check_in', 'is', null).order('date', { ascending: true }).order('id', { ascending: true }))
  const m = new Map()
  for (const r of rows) if (!m.has(r.agent_id)) m.set(r.agent_id, r.date)
  return m
}

// ─── Activity events (senTavus extension fill sessions) ───────────────────────
// Read-only here — rows are written by the extension/server, not this app.
// `dateFrom`/`dateTo` are local YYYY-MM-DD; converted to created_at timestamp
// bounds. Returns [] in local mode (no events without Supabase).
export async function getUserEvents({ dateFrom, dateTo } = {}) {
  if (!USE_SUPABASE) return []
  // Paginated (see fetchAllRows): a busy team posts >1000 events per multi-week range,
  // and an unpaginated fetch returned only the earliest 1000, making totals/records
  // SHRINK as the range grew. `id` is the stable tiebreaker for clean page boundaries.
  return fetchAllRows(() => {
    let q = supabase.from('user_events').select('*')
    if (dateFrom) q = q.gte('created_at', new Date(`${dateFrom}T00:00:00`).toISOString())
    if (dateTo)   q = q.lte('created_at', new Date(`${dateTo}T23:59:59.999`).toISOString())
    return q.order('created_at', { ascending: true }).order('id', { ascending: true })
  })
}

// ─── Published-per-day aggregate (the Stats tab's activity source, 2026-09-06) ─────────
// 🚨 WHY THIS EXISTS. The Stats tab used getUserEvents() above and counted posts client-side.
// Measured on 2026-09-06 for the default 90-day window: 31,297 rows × ~500 bytes = 16.8 MB,
// fetched as 32 SEQUENTIAL pages of 1,000 = 10.4 s — the entire "იტვირთება…" wait, spent
// downloading browser names, durations and offer URLs that were then reduced to one integer
// per agent per day. The table only started in June, so the window did not even trim it, and
// every month of use makes it slower.
//
// Returns rows ALREADY reduced: [{ user_email, day: 'YYYY-MM-DD', platform, n }] — only
// published=true events, which is all the Stats tab ever counted (computeActivity skipped the
// rest). Two paths, same shape:
//   1. RPC `stats_published_daily` (ops/scheduler-sql/2026-09-06-stats-published-daily.sql):
//      Postgres does the GROUP BY, ~a few hundred rows come back. Days are bucketed in
//      Asia/Tbilisi — the office's day, not the viewer's browser's.
//   2. If the RPC is not installed yet, the same reduction is done here from a SLIM column
//      set (user_email, platform, created_at), filtered to published rows and paged IN
//      PARALLEL — 28 pages in 0.7 s where the old fetch took 10 s. Days are bucketed in the
//      browser's local time, exactly as computeActivity did.
// Either way the caller cannot tell which path ran, so installing the RPC is a pure speed-up.
export async function getPublishedDaily({ dateFrom, dateTo } = {}) {
  if (!USE_SUPABASE) return []
  const since = dateFrom ? new Date(`${dateFrom}T00:00:00`).toISOString() : null
  // Exclusive end: the first instant of the day AFTER dateTo — no 23:59:59.999 edge.
  const until = dateTo ? new Date(new Date(`${dateTo}T00:00:00`).getTime() + 86400000).toISOString() : null

  // ⚠️ PAGED, WITH A STABLE ORDER. PostgREST applies its 1,000-row cap to set-returning
  // functions too: the first live call returned exactly 1,000 of 1,827 rows with no error, and
  // every agent's posts came out at roughly half (measured 2026-09-06, caught by the old-vs-new
  // diff, not by any test). The order clause is what makes pages non-overlapping.
  const rpcRows = []
  let rpcOk = true
  for (let offset = 0; ; offset += 1000) {
    const { data, error } = await supabase.rpc('stats_published_daily', { since, until })
      .order('user_email').order('day').order('platform').range(offset, offset + 999)
    if (error || !Array.isArray(data)) { rpcOk = false; break }
    rpcRows.push(...data)
    if (data.length < 1000) break
  }
  if (rpcOk) {
    return rpcRows.map(r => ({ user_email: r.user_email, day: r.day, platform: r.platform, n: Number(r.n) || 0 }))
  }
  // Fallback: the function is missing (PGRST202) or refused. Count how many rows there are,
  // then pull every page at once — the pages are independent, so serialising them was
  // only ever a habit of fetchAllRows, not a requirement.
  // ⚠️ `.select()` must be the FIRST call on the builder. Chaining a second select(…, {count,
  // head}) after the filters returns count: null with no error (measured 2026-09-06), which
  // made this path fetch zero pages and report every agent at 0 posts. The filters are applied
  // by one function to two differently-selected builders so the count and the pages cannot
  // disagree about the window.
  const where = q => {
    q = q.eq('published', true)
    if (since) q = q.gte('created_at', since)
    if (until) q = q.lt('created_at', until)
    return q
  }
  const { count, error: cErr } = await where(supabase.from('user_events').select('id', { count: 'exact', head: true }))
  if (cErr) throw cErr
  const PAGE = 1000
  const pages = Math.ceil((count || 0) / PAGE)
  const chunks = await Promise.all(Array.from({ length: pages }, (_, i) =>
    where(supabase.from('user_events').select('user_email, platform, created_at'))
      .order('created_at', { ascending: true }).order('id', { ascending: true })
      .range(i * PAGE, i * PAGE + PAGE - 1).then(({ data, error }) => { if (error) throw error; return data || [] })))
  const agg = new Map()
  for (const e of chunks.flat()) {
    const email = (e.user_email || '').trim().toLowerCase()
    if (!email) continue
    const d = new Date(e.created_at)
    const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    const k = `${email} ${day} ${e.platform || ''}`
    const row = agg.get(k) || { user_email: email, day, platform: e.platform || null, n: 0 }
    row.n++
    agg.set(k, row)
  }
  return [...agg.values()]
}

// ─── Deals (closed deals / earned money — Results pillar) ─────────────────────
// Entered by hand in the Agent modal. agent_id is the join key (unlike events).
const LS_DEALS = 'sva_deals_v1'

export async function getDeals({ agentId, dateFrom, dateTo } = {}) {
  if (!USE_SUPABASE) {
    let rows = lsRead(LS_DEALS)
    if (agentId)  rows = rows.filter(r => r.agent_id === agentId)
    if (dateFrom) rows = rows.filter(r => r.date >= dateFrom)
    if (dateTo)   rows = rows.filter(r => r.date <= dateTo)
    return rows
  }
  let q = supabase.from('deals').select('*')
  if (agentId)  q = q.eq('agent_id', agentId)
  if (dateFrom) q = q.gte('date', dateFrom)
  if (dateTo)   q = q.lte('date', dateTo)
  const { data, error } = await q.order('date', { ascending: false })
  if (error) throw error
  return data || []
}

export async function addDeal(record) {
  if (!USE_SUPABASE) {
    const row = { ...record, id: crypto.randomUUID(), created_at: new Date().toISOString() }
    lsWrite(LS_DEALS, [row, ...lsRead(LS_DEALS)])
    return row
  }
  const { data, error } = await supabase.from('deals').insert(record).select().single()
  if (error) throw error
  return data
}

export async function deleteDeal(id) {
  if (!USE_SUPABASE) {
    lsWrite(LS_DEALS, lsRead(LS_DEALS).filter(r => r.id !== id))
    return
  }
  const { error } = await supabase.from('deals').delete().eq('id', id)
  if (error) throw error
}

export async function upsertAttendance(record) {
  if (!USE_SUPABASE) {
    const rows = lsRead(LS_ATTENDANCE)
    const idx  = rows.findIndex(r => r.agent_id === record.agent_id && r.date === record.date)
    if (idx >= 0) rows[idx] = { ...rows[idx], ...record }
    else rows.push({ ...record, id: crypto.randomUUID() })
    lsWrite(LS_ATTENDANCE, rows)
    return rows.find(r => r.agent_id === record.agent_id && r.date === record.date)
  }
  const { data, error } = await supabase
    .from('attendance')
    .upsert(record, { onConflict: 'agent_id,date' })
    .select().single()
  if (error) throw error
  return data
}

export async function deleteAttendance(id) {
  if (!USE_SUPABASE) {
    lsWrite(LS_ATTENDANCE, lsRead(LS_ATTENDANCE).filter(r => r.id !== id))
    return
  }
  const { error } = await supabase.from('attendance').delete().eq('id', id)
  if (error) throw error
}

// ─── Internal ─────────────────────────────────────────────────────────────────
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']

async function _replaceSchedule(agentId, schedule) {
  await supabase.from('agent_schedule').delete().eq('agent_id', agentId)
  const rows = []
  DAYS.forEach(day => {
    const slots = Array.isArray(schedule[day]) ? schedule[day] : []
    slots.forEach(slot => {
      if (slot.start && slot.end) {
        rows.push({
          agent_id:    agentId,
          day_of_week: day,
          start_time:  slot.start,
          end_time:    slot.end,
          location:    slot.location || 'office',
        })
      }
    })
  })
  if (rows.length) {
    const { error } = await supabase.from('agent_schedule').insert(rows)
    if (error) throw error
  }
}

// ─── Schedule history ─────────────────────────────────────────────────────────
// Immutable snapshots — one row per schedule change, never updated or deleted.
// Shape: { id, agent_id, effective_from: 'YYYY-MM-DD', schedule: {...} }

async function _saveScheduleHistory(agentId, schedule, effectiveFrom) {
  const record = {
    id:             crypto.randomUUID(),
    agent_id:       agentId,
    effective_from: effectiveFrom,
    schedule:       schedule,
  }
  if (!USE_SUPABASE) {
    const rows = lsRead(LS_SCHEDULE_HIST)
    // Replace if same agent+date already exists (e.g. two saves on same day)
    const idx = rows.findIndex(r => r.agent_id === agentId && r.effective_from === effectiveFrom)
    if (idx >= 0) rows[idx] = record
    else rows.push(record)
    lsWrite(LS_SCHEDULE_HIST, rows)
    return
  }
  const { error } = await supabase
    .from('schedule_history')
    .upsert(record, { onConflict: 'agent_id,effective_from' })
  if (error) throw error
}

export async function getScheduleHistory(agentIds = []) {
  if (!USE_SUPABASE) {
    const rows = lsRead(LS_SCHEDULE_HIST)
    // a read only, like the Supabase branch below (attendance audit 2026-10-21): the old tomorrow->today rewrite moved
    // real future schedule changes a day early here too
    if (agentIds.length === 0) return rows
    return rows.filter(r => agentIds.includes(r.agent_id))
  }
  // ⚠️ A READ, nothing else (audit 2026-10-21). This used to upsert every tomorrow-dated row with no
  // today row back to today - a repair for an old save bug - on every read, from any page that loaded attendance
  // (the Activity tab included), and to rewrite the returned row's date even when that write failed. It cannot tell
  // a legitimate schedule change that starts tomorrow from the old bug, so it silently moved real future changes
  // a day early. Do not put it back: a historical repair belongs in a one-off, audited migration.
  let q = supabase.from('schedule_history').select('*').order('effective_from', { ascending: true })
  if (agentIds.length > 0) q = q.in('agent_id', agentIds)
  const { data, error } = await q
  if (error) throw error
  return data
}

// ─── Schedule overrides ───────────────────────────────────────────────────────
// One-off date overrides that sit on top of the base schedule.
// Shape: { id, agent_id, date, slots: [{start,end,location}], reason, created_by, created_at }
// slots=[] means the agent is off that specific date.
const LS_OVERRIDES = 'sva_overrides_v1'

export async function getOverrides({ agentId, dateFrom, dateTo } = {}) {
  if (!USE_SUPABASE) {
    let rows = lsRead(LS_OVERRIDES)
    if (agentId)  rows = rows.filter(r => r.agent_id === agentId)
    if (dateFrom) rows = rows.filter(r => r.date >= dateFrom)
    if (dateTo)   rows = rows.filter(r => r.date <= dateTo)
    return rows
  }
  let q = supabase.from('schedule_overrides').select('*')
  if (agentId)  q = q.eq('agent_id', agentId)
  if (dateFrom) q = q.gte('date', dateFrom)
  if (dateTo)   q = q.lte('date', dateTo)
  const { data, error } = await q
  if (error) throw error
  return data
}

export async function upsertOverride(record) {
  if (!USE_SUPABASE) {
    const rows = lsRead(LS_OVERRIDES)
    const idx  = rows.findIndex(r => r.agent_id === record.agent_id && r.date === record.date)
    if (idx >= 0) rows[idx] = { ...rows[idx], ...record }
    else rows.push({ ...record, id: crypto.randomUUID() })
    lsWrite(LS_OVERRIDES, rows)
    return rows.find(r => r.agent_id === record.agent_id && r.date === record.date)
  }
  const { data, error } = await supabase
    .from('schedule_overrides')
    .upsert(record, { onConflict: 'agent_id,date' })
    .select().single()
  if (error) throw error
  return data
}

export async function deleteOverride(id) {
  if (!USE_SUPABASE) {
    lsWrite(LS_OVERRIDES, lsRead(LS_OVERRIDES).filter(r => r.id !== id))
    return
  }
  const { error } = await supabase.from('schedule_overrides').delete().eq('id', id)
  if (error) throw error
}

// ─── Candidates ───────────────────────────────────────────────────────────────
/**
 * Candidate shape:
 * {
 *   id, name, email, phone,
 *   position,           // role they applied for
 *   source,             // 'referral' | 'linkedin' | 'website' | 'other'
 *   status,             // 'new' | 'scheduled' | 'interviewed' | 'hired' | 'rejected'
 *   interview_date,     // 'YYYY-MM-DD'
 *   interview_time,     // 'HH:MM'
 *   interviewer,        // free text
 *   notes,              // free text
 *   hired_agent_id,     // set when converted to agent
 *   created_at
 * }
 */
const LS_CANDIDATES = 'sva_candidates_v1'

export async function getCandidates() {
  if (!USE_SUPABASE) return lsRead(LS_CANDIDATES)
  // Paginated: the candidate pool grows unbounded over time and would silently cap at
  // 1000. `id` is the stable tiebreaker (created_at alone isn't unique).
  return fetchAllRows(() =>
    supabase.from('candidates').select('*')
      .order('created_at', { ascending: false }).order('id', { ascending: true }))
}

// Self-filled applications nobody has acted on yet — the Desk's new-applicant banner
// (NewApplicantsBanner) and nothing else. NOT a general "new candidates" reader:
//
//   apply_context is not null  → the row came from the PUBLIC vacancy form. A candidate a
//                                manager typed in needs no announcement to the manager who
//                                typed it. This is why the arrival evidence is a column of
//                                its own (scheduler migration 0013) rather than a source label.
//   status = 'new' AND no interview_date → nobody has worked it. These two ARE the dismissal:
//                                there is no read-state anywhere, so the row leaves the banner
//                                the moment somebody books, rejects or advances the candidate.
//   14 days                    → a floor, and it has the same weakness 0431's does: it bounds
//                                the noise, and an application older than this stops being
//                                announced even if it was never called. The Candidates tab is
//                                still the full list; this is a nudge, not the queue.
export async function getNewApplicants({ days = 14, limit = 20 } = {}) {
  if (!USE_SUPABASE) return []
  const since = new Date(Date.now() - days * 86400000).toISOString()
  const { data, error } = await supabase.from('candidates')
    .select('id,name,phone,position,source,created_at,interviewer,created_by_name')
    .eq('status', 'new')
    .is('interview_date', null)
    .not('apply_context', 'is', null)
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

// ── Posted vacancy links (scheduler migration 0017) ─────────────────────────────────────────
// One row per link the office posts somewhere (/vacancy?l=<id>): which platform, WHY it exists
// (the comment is required — a link nobody can identify is a link nobody dares block), who made
// it, and whether it is blocked. The public page never reads this table; api.sentavus.com does,
// fresh on every submission, so a block takes effect on the very next attempt.
//
// ⚠️ `blocked_at` IS the status — there is no status column. A blocked link keeps its row and
// its history: the applications it let in stay attributable to it (candidates.vacancy_link_id).

// `agentId` narrows to one creator (a manager sees their own links); omit it to list all
// (admin, general manager). Newest first; small by nature — links are made by hand.
export async function listVacancyLinks({ agentId = null, limit = 50 } = {}) {
  if (!USE_SUPABASE) return []
  let q = supabase.from('vacancy_link')
    .select('id,platform,comment,created_by,created_by_name,manager_id,manager_name,created_at,blocked_at,blocked_by_name,block_reason')
    .order('created_at', { ascending: false }).order('id', { ascending: true })
    .limit(limit)
  if (agentId) q = q.or(`created_by.eq.${agentId},manager_id.eq.${agentId}`)
  const { data, error } = await q
  if (error) throw error
  return data || []
}

// Applications per link, counted from the candidates themselves rather than stored on the link:
// a stored counter is a second copy of a fact that can drift, and this is what "what did this
// link let in" has to mean.
export async function countApplicationsByVacancyLink(ids = []) {
  if (!USE_SUPABASE || !ids.length) return {}
  const { data, error } = await supabase.from('candidates')
    .select('vacancy_link_id').in('vacancy_link_id', ids)
  if (error) throw error
  const out = {}
  for (const r of data || []) out[r.vacancy_link_id] = (out[r.vacancy_link_id] || 0) + 1
  return out
}

// The creator is stamped HERE, not chosen in the form, for the same reason useCandidates.create
// stamps authorship: the person who makes a link becomes the recruiter on every application it
// brings, and that must not be a field someone can set to a colleague. An admin has no agent row
// (me = null), so their links bring unassigned applicants — the owner's own rule.
export async function createVacancyLink({ platform, comment, me = null, adminName = null, manager = null }) {
  const row = {
    platform: String(platform || '').trim().slice(0, 40),
    comment: String(comment || '').trim().slice(0, 300),
    created_by: me?.id || null,
    // adminName is the caller's fallback label (an admin has no agent row). It is passed in
    // rather than read here: the db layer does not import the log module, which imports it.
    created_by_name: me?.name || adminName || null,
    // 0020: the manager the link's applicants are ASSIGNED to. Null = unassigned (any manager
    // may take them), which is what an admin's link did before a manager could be named.
    manager_id: manager?.id || null,
    manager_name: manager?.name || null,
  }
  if (!row.platform || !row.comment) throw new Error('platform and comment are required')
  const { data, error } = await supabase.from('vacancy_link').insert(row).select().single()
  if (error) throw error
  return data
}

// Block or unblock. Unblocking clears the reason as well as the time: a link that is active
// again should not keep saying why it once was not.
// The switch that makes a block a lock. Read DIRECTLY, not through getConfig(): that function
// returns only keys declared in CONFIG_DEFAULTS, so this key would always read as unset and the
// checkbox would show OFF over a policy that is ON — a lock that looks open is the worse lie.
export async function getVacancyLinkPolicy() {
  if (!USE_SUPABASE) return {}
  const { data, error } = await supabase.from('config').select('values').eq('key', 'vacancy_link_policy').maybeSingle()
  if (error) throw error
  const v = data?.values
  return v && typeof v === 'object' && !Array.isArray(v) ? v : {}
}

// Who an admin can hand a posted link to: active agents whose job title is Manager. The title,
// not the permission tier — app_users.role is not readable from the client and is not what
// "manager" means on a candidate card.
export async function listVacancyLinkManagers() {
  if (!USE_SUPABASE) return []
  const { data, error } = await supabase.from('agents')
    .select('id,name').eq('is_active', true).eq('role', 'Manager').order('name')
  if (error) throw error
  return data || []
}

export async function setVacancyLinkBlocked(id, blocked, { byName = null, reason = null } = {}) {
  const patch = blocked
    ? { blocked_at: new Date().toISOString(), blocked_by_name: byName || null, block_reason: reason || null }
    : { blocked_at: null, blocked_by_name: null, block_reason: null }
  const { data, error } = await supabase.from('vacancy_link').update(patch).eq('id', id).select().single()
  if (error) throw error
  return data
}

export async function addCandidate(data) {
  if (!USE_SUPABASE) {
    const item = { ...data, id: crypto.randomUUID(), created_at: new Date().toISOString() }
    lsWrite(LS_CANDIDATES, [item, ...lsRead(LS_CANDIDATES)])
    return item
  }
  const { data: row, error } = await supabase.from('candidates').insert(data).select().single()
  if (error) throw error
  return row
}

export async function updateCandidate(id, updates) {
  if (!USE_SUPABASE) {
    const rows = lsRead(LS_CANDIDATES).map(c => c.id === id ? { ...c, ...updates } : c)
    lsWrite(LS_CANDIDATES, rows)
    return rows.find(c => c.id === id)
  }
  const { data, error } = await supabase.from('candidates').update(updates).eq('id', id).select().single()
  if (error) throw error
  return data
}

export async function deleteCandidate(id) {
  if (!USE_SUPABASE) {
    lsWrite(LS_CANDIDATES, lsRead(LS_CANDIDATES).filter(c => c.id !== id))
    return
  }
  const { error } = await supabase.from('candidates').delete().eq('id', id)
  if (error) throw error
}

// ─── Config (admin-managed lists) ────────────────────────────────────────────
// Each config row: { key: 'agent_roles', values: ['Agent', 'Senior Agent', ...] }
const LS_CONFIG = 'sva_config_v1'

const CONFIG_DEFAULTS = {
  agent_roles:       ['Agent', 'Senior Agent', 'Junior Agent', 'Manager', 'Admin'],
  districts:         ['ვაკე', 'საბურთალო', 'ისანი', 'სამგორი', 'გლდანი', 'ნაძალადევი', 'დიდუბე', 'ჩუღურეთი', 'მთაწმინდა', 'კრწანისი'],
  candidate_sources: ['Facebook', 'LinkedIn', 'Referral', 'Website', 'Other'],
  languages:         ['Georgian', 'English', 'Russian'],
  // (performance_weights retired 2026-07-12 — the Stats composite now IS the
  // desk ranking score below; the old config row may still exist in the DB but
  // nothing reads it.)
  // Default commission % per role (jsonb map { role: percent }). Auto-fills the
  // agent's commission when a role is picked; the agent value can override it.
  role_commissions: {},
  // Ranking points (weekly base) — THE scoring config: one engine
  // (lib/rankingScore.js) drives both the Desk leaderboard and the Stats tab's
  // performance score. Caps stop reshare/scrape spam from outrunning deals;
  // attendance is a small positive-only bonus, never shown as a public metric.
  // Views scale the caps by window length (capScaleForDays).
  desk_ranking_points: { close: 150, commission_per_100: 1, meeting: 15, scrape: 5, scrape_cap: 50, reshare: 1, reshare_cap: 100, office_day: 4, perfect_week: 15 },
  // FX rates → GEL per 1 unit; used wherever money feeds a score or a total
  // (rankingScore.toGel, computeResultsFromViewings). GEL itself is always 1.
  fx_to_gel: { USD: 2.70, EUR: 3.10 },
  // CO-BROKE (0449) — the share WE keep when the client is represented by an agent from
  // another agency. Owner, 2026-08-06: "the co-broke split is defined by the agency
  // itself; the standard is 50% right now and will change when needed."
  //
  // ⚠️ THIS IS THE STARTING VALUE, NOT THE ANSWER. It is COPIED onto the meeting when a
  // split is recorded and the agent may negotiate it away from here per deal. Nothing may
  // read this key to DISPLAY a past deal's split: the day it moves to 60, a live read
  // would re-price every closed deal — income, leaderboard and paid-out history all
  // shifting under numbers people were already paid against. Same reason
  // viewing_agents.commission_pct is snapshotted at close.
  //
  // It being a declared agency POLICY is also what makes pre-filling it legitimate under
  // the 2026-07-25 R6 ruling, which forbids the SYSTEM suggesting a split. R6 bars a
  // machine guess; this is the agency's own standard, with an owner who can change it.
  cobroke_default_percent: { us: 50 },
}

export async function getConfig() {
  if (!USE_SUPABASE) {
    const rows = lsRead(LS_CONFIG)
    // Seed defaults for any missing keys
    const result = {}
    Object.entries(CONFIG_DEFAULTS).forEach(([key, def]) => {
      const row = rows.find(r => r.key === key)
      result[key] = row ? row.values : def
    })
    return result
  }
  const { data, error } = await supabase.from('config').select('*')
  if (error) throw error
  const result = {}
  Object.entries(CONFIG_DEFAULTS).forEach(([key, def]) => {
    const row = data.find(r => r.key === key)
    result[key] = row ? row.values : def
  })
  return result
}

// 🚨 KEYS WHOSE ROW IS SHARED BY WRITERS THAT CANNOT SEE EACH OTHER (B603).
// `crawler_settings` is one jsonb blob read by the re-check crawler, discovery, the
// complex linker and the match materializer, and written by BOTH the Admin panel and
// `core/ops/tune_discovery.py`. Every editing card already merges over the value it was
// handed — and that was not enough, because the value it was handed is the snapshot from
// PAGE LOAD. Measured sequence, 2026-08-08:
//   T0  admin opens the panel        → snapshot has no discoveryMaxNewPerTick
//   T1  tune_discovery.py writes 1200 (+ 3 sibling keys) straight to the row
//   T2  admin edits batchSize, saves → writes T0's snapshot back; all four keys GONE
// The worker then fell back to module defaults — 300 instead of 1200 — and every layer
// looked healthy, because an absent key is indistinguishable from a deliberate default.
// Ten days passed before an arithmetic audit of the tallies noticed.
// So for these keys the caller's object is a PATCH, not a replacement: re-read the row
// at the moment of writing and merge the patch over what is actually stored.
//
// ⚠️ MERGE IS OPT-IN, AND IT MUST STAY THAT WAY. `role_commissions` is edited by BUILDING
// A NEW OBJECT WITH A KEY DELETED (AdminTab's RoleCommissionEditor) — merging there would
// silently resurrect the deleted role every time. A key belongs on this list only when its
// row has several independent writers AND removal is not an operation anyone performs.
//
// ⚠️ THIS IS NOT ATOMIC and must not be described as such. It shrinks the window from the
// page's lifetime (hours) to one round trip (~100 ms). Closing it completely needs a
// `values = config.values || excluded.values` upsert, i.e. an RPC in the SCHEDULER
// database — no DDL path to that project exists from this environment today (direct 5432
// is unreachable from the app box; the MCP is core-only). The residual window is covered
// instead by DETECTION: lib/listing_discovery.js records the key set on every heartbeat
// and names any key that was present last tick and is missing now. See B603.
const CONFIG_MERGE_KEYS = new Set(['crawler_settings'])

export async function setConfigKey(key, values) {
  if (!USE_SUPABASE) {
    const rows = lsRead(LS_CONFIG)
    const idx = rows.findIndex(r => r.key === key)
    if (idx >= 0) rows[idx] = { key, values }
    else rows.push({ key, values })
    lsWrite(LS_CONFIG, rows)
    return { key, values }
  }
  let next = values
  if (CONFIG_MERGE_KEYS.has(key) && values && typeof values === 'object' && !Array.isArray(values)) {
    // Read failure must NOT silently degrade to a replacing write — that is the very bug.
    // Refuse instead: an unsaved setting the admin can retry beats four keys deleted.
    const { data: cur, error: readErr } = await supabase
      .from('config').select('values').eq('key', key).maybeSingle()
    if (readErr) throw new Error(`could not read ${key} before saving (not saved): ${readErr.message}`)
    const stored = cur?.values
    if (stored && typeof stored === 'object' && !Array.isArray(stored)) next = { ...stored, ...values }
  }
  const { data, error } = await supabase
    .from('config')
    .upsert({ key, values: next }, { onConflict: 'key' })
    .select().single()
  if (error) throw error
  return data
}

// ─── Phone inventory ──────────────────────────────────────────────────────────
// phone_numbers table: { id, number, agent_id (nullable) }
const LS_PHONES = 'sva_phones_v1'

export async function getPhoneNumbers() {
  if (!USE_SUPABASE) return lsRead(LS_PHONES)
  const { data, error } = await supabase.from('phone_numbers').select('*').order('number')
  if (error) throw error
  return data
}

export async function addPhoneNumber(number, note = '') {
  if (!USE_SUPABASE) {
    const rows = lsRead(LS_PHONES)
    if (rows.find(r => r.number === number)) throw new Error('Number already exists')
    const row = { id: crypto.randomUUID(), number, note: note || null, banned: false, agent_id: null }
    lsWrite(LS_PHONES, [...rows, row])
    return row
  }
  const { data, error } = await supabase.from('phone_numbers').insert({ number, note: note || null, banned: false, agent_id: null }).select().single()
  if (error) throw error
  return data
}

export async function updatePhoneNumber(id, updates) {
  if (!USE_SUPABASE) {
    const rows = lsRead(LS_PHONES).map(r => r.id === id ? { ...r, ...updates } : r)
    lsWrite(LS_PHONES, rows)
    return rows.find(r => r.id === id)
  }
  const { data, error } = await supabase.from('phone_numbers').update(updates).eq('id', id).select().single()
  if (error) throw error
  return data
}

export async function deletePhoneNumber(id) {
  if (!USE_SUPABASE) {
    lsWrite(LS_PHONES, lsRead(LS_PHONES).filter(r => r.id !== id))
    return
  }
  const { error } = await supabase.from('phone_numbers').delete().eq('id', id)
  if (error) throw error
}

// ─── eSIM QR files per number (scheduler 0038) ────────────────────────────────
// Private bucket `phone-esim`, object at <phone_numbers.id>/<uuid>.<ext>. RLS mirrors phone_numbers:
// a viewer gets the files of exactly the numbers they can see. Never deleted — "replace" uploads the
// new file and RETIRES the live ones, which stay in the number's history.
// ⚠️ Since 2026-10-24 the FILE goes through api.sentavus.com (/api/scheduler/esim/*, server/lib/scheduler_esim.js),
// not supabase.storage: the files move to R2 with the Hetzner move, and the browser must never hold R2 keys. The
// server re-checks the same two rules (company may upload; anyone who sees the number may open) as this user.

export async function getPhoneFiles() {
  if (!USE_SUPABASE) return []
  const { data, error } = await supabase.from('phone_number_file').select('*').order('issued_at', { ascending: false })
  if (error) throw error
  return data || []
}

// Upload one file for a number. replace=true then retires the number's other live files — after the
// upload succeeded, so a failed upload never leaves the number without a QR.
export async function uploadPhoneFile(phoneId, file, { replace = false } = {}) {
  if (!USE_SUPABASE) throw new Error('offline')
  const ext = (file.name.split('.').pop() || 'pdf').toLowerCase()
  const up = await fetch(`${SNTV_API}/api/scheduler/esim/upload?phone_id=${encodeURIComponent(phoneId)}&ext=${encodeURIComponent(ext)}`, {
    method: 'POST', headers: { ...(await schedHeaders()), 'Content-Type': file.type || 'application/pdf' }, body: file })
  const uj = await up.json().catch(() => ({}))
  if (!up.ok || !uj.path) throw new Error(uj.error || `upload failed (${up.status})`)
  const path = uj.path
  const { data, error } = await supabase.from('phone_number_file')
    .insert({ phone_number_id: phoneId, storage_path: path, file_name: file.name }).select().single()
  if (error) throw error
  if (replace) {
    const { error: e2 } = await supabase.from('phone_number_file')
      .update({ retired_at: new Date().toISOString() })
      .eq('phone_number_id', phoneId).is('retired_at', null).neq('id', data.id)
    if (e2) throw e2
  }
  return data
}

export async function setPhoneFileRetired(id, retired) {
  const { data, error } = await supabase.from('phone_number_file')
    .update({ retired_at: retired ? new Date().toISOString() : null }).eq('id', id).select().single()
  if (error) throw error
  return data
}

// A QR is a credential: open it through a short-lived signed URL, never a public one.
export async function phoneFileUrl(storagePath) {
  const r = await fetch(`${SNTV_API}/api/scheduler/esim/link?path=${encodeURIComponent(storagePath)}`, { headers: await schedHeaders() })
  const j = await r.json().catch(() => ({}))
  if (!r.ok || !j.url) throw new Error(j.error || `file unavailable (${r.status})`)
  return j.url
}

// ─── portal logins per number (scheduler 0039) ────────────────────────────────
// myhome.ge / ss.ge accounts registered with a work number. Password lives in Vault; the table has no
// client grants, so these RPCs are the only door, and each decides access per number (company, the
// number's office manager, or its current holder).
export async function getPhoneLogins(phoneIds = null) {
  if (!USE_SUPABASE) return []
  const { data, error } = await supabase.rpc('phone_logins_for', { p_phone_ids: phoneIds })
  if (error) throw error
  return data || []
}

export async function getMyPhoneLogins() {
  if (!USE_SUPABASE) return []
  const { data, error } = await supabase.rpc('my_phone_logins')
  if (error) throw error
  return data || []
}

// password: undefined/null = keep the current one, '' = clear it.
export async function setPhoneLogin(phoneId, platform, { login, password = null, note = null, platformLabel = null }) {
  const { data, error } = await supabase.rpc('set_phone_login', {
    p_phone_id: phoneId, p_platform: platform, p_login: login ?? null,
    p_password: password ?? null, p_note: note, p_platform_label: platformLabel })
  if (error) throw error
  return data
}

export async function assignPhoneNumber(id, agentId, agentName = null) {
  // agentId = null to unassign
  if (!USE_SUPABASE) {
    const rows = lsRead(LS_PHONES)
    // Clear from any other agent first
    const cleared = rows.map(r => r.agent_id === agentId && agentId ? { ...r, agent_id: null } : r)
    const updated = cleared.map(r => r.id === id ? { ...r, agent_id: agentId } : r)
    lsWrite(LS_PHONES, updated)
    return updated.find(r => r.id === id)
  }
  // Get the phone number string before updating
  const { data: phone } = await supabase.from('phone_numbers').select('number').eq('id', id).single()
  // Clear from previous owner first
  if (agentId) {
    await supabase.from('phone_numbers').update({ agent_id: null }).eq('agent_id', agentId)
  }
  const { data, error } = await supabase.from('phone_numbers')
    .update({ agent_id: agentId }).eq('id', id).select().single()
  if (error) throw error
  // Log history
  if (phone?.number) await logPhoneAssignment(phone.number, agentId, agentName)
  return data
}

export async function setPhoneAgentByNumber(number, agentId, agentName = null, logHistory = true) {
  if (!number) return
  if (!USE_SUPABASE) {
    const rows = lsRead(LS_PHONES)
    const cleared = rows.map(r => r.agent_id === agentId && agentId ? { ...r, agent_id: null } : r)
    const updated = cleared.map(r => r.number === number ? { ...r, agent_id: agentId } : r)
    lsWrite(LS_PHONES, updated)
    return
  }
  if (agentId) {
    await supabase.from('phone_numbers').update({ agent_id: null }).eq('agent_id', agentId)
  }
  await supabase.from('phone_numbers').update({ agent_id: agentId }).eq('number', number)
  if (logHistory) await logPhoneAssignment(number, agentId, agentName)
}

// ─── phone_history table ──────────────────────────────────────────────────────
// { id, phone_number, agent_id, agent_name, assigned_at, unassigned_at }

export async function logPhoneAssignment(phoneNumber, agentId, agentName) {
  if (!USE_SUPABASE) return
  // Close any open history entry for this number
  await supabase.from('phone_history')
    .update({ unassigned_at: new Date().toISOString() })
    .eq('phone_number', phoneNumber)
    .is('unassigned_at', null)
  // Open a new entry if assigning (not unassigning)
  if (agentId && agentName) {
    await supabase.from('phone_history').insert({
      phone_number: phoneNumber,
      agent_id: agentId,
      agent_name: agentName,
      assigned_at: new Date().toISOString(),
      unassigned_at: null,
    })
  }
}

// phone_history.agent_name is a snapshot taken at assignment time — it does NOT
// track renames. Call this after an agent's name changes so their currently-open
// history row (the one shown as "current") doesn't keep displaying the old name.
export async function renameOpenPhoneHistory(agentId, newName) {
  if (!USE_SUPABASE || !agentId || !newName) return
  await supabase.from('phone_history')
    .update({ agent_name: newName })
    .eq('agent_id', agentId)
    .is('unassigned_at', null)
}

// Most-recent number each agent was ever assigned, from history. Returns
// { [agent_id]: phone_number }. Lets a resigned agent still display the number
// they had even after it was removed from their record (phone_work cleared).
export async function getLatestPhoneByAgent() {
  if (!USE_SUPABASE) return {}
  const { data, error } = await supabase
    .from('phone_history')
    .select('phone_number, agent_id, assigned_at')
    .order('assigned_at', { ascending: false })
  if (error) throw error
  const map = {}
  for (const row of data) {
    if (row.agent_id && !map[row.agent_id]) map[row.agent_id] = row.phone_number
  }
  return map
}

export async function getPhoneHistory(phoneNumber) {
  if (!USE_SUPABASE) return []
  const { data, error } = await supabase
    .from('phone_history')
    .select('*')
    .eq('phone_number', phoneNumber)
    .order('assigned_at', { ascending: false })
  if (error) throw error
  return data
}

// All ownership history, grouped by phone number (newest assignment first within
// each). Lets the admin phone table render an inline ownership timeline per number
// without one query per row.
export async function getAllPhoneHistory() {
  if (!USE_SUPABASE) return {}
  const { data, error } = await supabase
    .from('phone_history')
    .select('*')
    .order('assigned_at', { ascending: false })
  if (error) throw error
  const map = {}
  for (const row of data) (map[row.phone_number] ||= []).push(row)
  return map
}

// ─── Telegram notifications (scheduler 0017) ──────────────────────────────────────────────────
// Every call below is an RPC that resolves the caller from the session; the tables behind them are
// readable only by their owner and writable only through these functions (the bot token by admin).
async function rpc(name, args) {
  if (!USE_SUPABASE) throw new Error('offline')
  const { data, error } = await supabase.rpc(name, args || {})
  if (error) throw error
  return data
}
export const getMyNotifyState    = () => rpc('my_notify_state')
export const notifyLinkStart     = () => rpc('notify_link_start')
export const notifyUnlink        = () => rpc('notify_unlink')
export const notifySendTest      = () => rpc('notify_send_test')
export const notifySetPref       = (key, enabled) => rpc('notify_set_pref', { p_key: key, p_enabled: enabled })
// 0022: on events that offer one (deal_closed): 'all' of senTavus or my 'office'.
export const notifySetPrefScope  = (key, scope) => rpc('notify_set_pref_scope', { p_key: key, p_scope: scope })
// 0023: on events that offer a reminder lead time (interview_reminder, default 15).
export const notifySetPrefMinutes = (key, minutes) => rpc('notify_set_pref_minutes', { p_key: key, p_minutes: minutes })
export const notifySetDelivery   = ({ quietEnabled, quietStart, quietEnd, paused }) =>
  rpc('notify_set_delivery', { p_quiet_enabled: quietEnabled, p_quiet_start: quietStart, p_quiet_end: quietEnd, p_paused: paused })
export const telegramBotSetToken = token => rpc('telegram_bot_set_token', { p_token: token })
// 0018 — "tell me when a new lead matches this client". The row stores the leads-search query the
// client modal builds; the server notifier replays it as the agent and checks access every cycle.
export async function getClientLeadWatches() {
  if (!USE_SUPABASE) return []
  const { data, error } = await supabase.from('client_lead_watch')
    .select('id,requirement_id,client_name,summary,muted,created_at,last_checked_at,last_match_at,match_count,access_ok,search_qs')
    .order('created_at', { ascending: false })
  if (error) throw error
  return data
}
export async function getClientLeadWatch(requirementId) {
  if (!USE_SUPABASE) return null
  const { data, error } = await supabase.from('client_lead_watch')
    .select('id,muted,search_qs,access_ok').eq('requirement_id', requirementId).maybeSingle()
  if (error) throw error
  return data
}
export const clientLeadWatchSet = ({ requirementId, clientName, summary, searchQs }) =>
  rpc('client_lead_watch_set', { p_requirement_id: requirementId, p_client_name: clientName || null, p_summary: summary || null, p_search_qs: searchQs })
export const clientLeadWatchMute   = (id, muted) => rpc('client_lead_watch_mute', { p_id: id, p_muted: muted })
export const clientLeadWatchDelete = id => rpc('client_lead_watch_delete', { p_id: id })
export async function getRecentNotifications(limit = 15) {
  if (!USE_SUPABASE) return []
  const { data, error } = await supabase.from('notify_outbox')
    .select('id,event_key,body,created_at,send_after,sent_at,last_error')
    .order('created_at', { ascending: false }).limit(limit)
  if (error) throw error
  return data
}

// 0015 — every change of a number's OFFICE, newest first, grouped by phone_numbers.id. Written only
// by DB triggers (never by the client); RLS returns only rows for numbers the viewer may see.
export async function getPhoneOfficeHistory() {
  if (!USE_SUPABASE) return {}
  const { data, error } = await supabase
    .from('phone_office_history')
    .select('*')
    .order('changed_at', { ascending: false })
  if (error) throw error
  const map = {}
  for (const row of data) if (row.phone_number_id) (map[row.phone_number_id] ||= []).push(row)
  return map
}

// 0015 — the viewer's office scope as the DATABASE resolves it (live office_manager coverage):
// { scope: 'company'|'offices'|'self'|'none', role, office_ids, offices:[{id,name}] }.
// For LABELLING and the office switcher only — RLS is what actually narrows the rows.
export async function getMyOfficeScope() {
  if (!USE_SUPABASE) return { scope: 'company', role: null, office_ids: [], offices: [] }
  const { data, error } = await supabase.rpc('my_office_scope')
  if (error) throw error
  return data
}

// ─── Revert hire ──────────────────────────────────────────────────────────────
// Standalone function — deletes the agent and restores candidate to pre-hire status.
// Called from both useCandidates.revertHire and AgentModal.
export async function revertCandidateHire(candidateId, agentId, preHireStatus) {
  await deleteAgent(agentId)
  return updateCandidate(candidateId, {
    status:          preHireStatus || 'new',
    hired_agent_id:  null,
    pre_hire_status: null,
  })
}

// ─── Extension config (admin_config row id=1) ─────────────────────────────────
// The senTavus extension's popup config. The effective config (DB-preferred
// settings + chip universe from badge maps) is served by the API server; we read
// it from there so the editor always shows what the extension actually sees.
// Saving writes the settings array + chip_overrides back to the admin_config row
// and bumps `version` (timestamp) so every extension re-fetches on next popup open.
//
// admin_config lives in THIS Supabase project (the same one the scheduler uses).
const SNTV_API = 'https://api.sentavus.com'

export async function getExtensionConfig() {
  // ?fresh=1 bypasses the server's 5-min cache so the editor always reflects the
  // latest save immediately (the extension itself uses the cached path).
  const r = await fetch(`${SNTV_API}/api/admin/config?fresh=1`)
  if (!r.ok) throw new Error(`Failed to load extension config (HTTP ${r.status})`)
  return r.json()   // { version, settings:[...], chips:{ myhome:[...], ss:[...] } }
}

export async function saveExtensionConfig({ settings, chipOverrides }) {
  if (!USE_SUPABASE) throw new Error('Supabase connection required to save extension config')
  // Unique, sortable version string → guarantees the extension sees a change and re-fetches.
  const version = new Date().toISOString().replace(/[:.]/g, '-')
  const { error } = await supabase
    .from('admin_config')
    .update({ settings, chip_overrides: chipOverrides, version })
    .eq('id', 1)
  if (error) throw error
  return version
}

// ─── App versions (extension build registry) ──────────────────────────────────
// app_versions: one row per build ever sighted { app_version, first_seen, status, note }.
// "latest" = the build agents are told to run = newest first_seen where status='active'.
// Marking a faulty build 'revoked' here makes the previous good build become latest
// again automatically — BOTH readers filter status='active': this app's ListingsTab
// and the server's GET /api/test/latest-version. Writes run as `authenticated`
// (admin is logged in). See app-versions-schema.sql for the column + RLS migration.
export async function getAppVersions() {
  if (!USE_SUPABASE) return []
  const { data, error } = await supabase
    .from('app_versions')
    .select('app_version, first_seen, status, note')
    .order('first_seen', { ascending: false })
  if (error) throw error
  return data || []
}

// status: 'active' | 'revoked'. note is optional (only written when provided).
export async function setVersionStatus(appVersion, status, note) {
  if (!USE_SUPABASE) throw new Error('Supabase connection required')
  const patch = { status }
  if (note !== undefined) patch.note = note
  const { error } = await supabase
    .from('app_versions').update(patch).eq('app_version', appVersion)
  if (error) throw error
}

// Current count of agents on each version, from agent_app (who is on what right now).
// Lets the admin avoid revoking a build the fleet is still running. → { version: count }
export async function getAgentVersionCounts() {
  if (!USE_SUPABASE) return {}
  const { data, error } = await supabase.from('agent_app').select('app_version')
  if (error) throw error
  const m = {}
  for (const r of data || []) if (r.app_version) m[r.app_version] = (m[r.app_version] || 0) + 1
  return m
}

// ─── Monitor findings (platform schema drift) ─────────────────────────────────
// Written by the server's /api/monitor/report (auto-learn). Admin reviews here.
export async function getMonitorFindings() {
  if (!USE_SUPABASE) return []
  const { data, error } = await supabase
    .from('monitor_findings').select('*').order('last_seen', { ascending: false })
  if (error) throw error
  return data || []
}

// AI health alerts — kind='ai_alert' rows the server raises ONLY when the AI is
// genuinely broken (rejected key / quota-funds exhausted / 5-failure streak), never
// for one-off errors (see server.js _aiHealthTrack). Surfaced to ADMINS in the Desk
// notification bell; dismissable from Admin → AI Quality (setFindingStatus). 2026-07-29.
export async function getAiAlerts() {
  if (!USE_SUPABASE) return []
  const since = new Date(Date.now() - 14 * 86400e3).toISOString()
  const { data, error } = await supabase
    .from('monitor_findings')
    .select('finding_key, platform, detail, sample, last_seen')
    .eq('kind', 'ai_alert').eq('status', 'new').gte('last_seen', since)
    .order('last_seen', { ascending: false }).limit(10)
  if (error) throw error
  return data || []
}

export async function setFindingStatus(findingKey, status) {
  if (!USE_SUPABASE) return
  const { error } = await supabase
    .from('monitor_findings').update({ status }).eq('finding_key', findingKey)
  if (error) throw error
}

// ─── Checker cadence (adaptive analyst controller) ────────────────────────────
// Read-only here. The server's controller writes `interval`/`last_run` on each analyst run;
// we surface it so the admin can SEE how often the analyst is checking. Returns null until the
// analyst has run at least once (no row yet).
export async function getCheckerState(name = 'analyst') {
  if (!USE_SUPABASE) return null
  const { data, error } = await supabase
    .from('checker_state').select('*').eq('name', name).maybeSingle()
  if (error) throw error
  return data || null
}

// ─── Fill overrides (what agents corrected after the filler) ──────────────────
// Read-only. Written by the server when an agent changes a filler-filled value before publish.
// Returns recent rows + per-field counts so the admin sees where the filler/AI is most wrong.
export async function getFillOverrides({ limit = 100 } = {}) {
  if (!USE_SUPABASE) return { recent: [], byField: [] }
  const { data, error } = await supabase
    .from('fill_overrides').select('*').order('created_at', { ascending: false }).limit(limit)
  if (error) throw error
  const rows = data || []
  const counts = {}
  for (const r of rows) counts[r.field] = (counts[r.field] || 0) + 1
  const byField = Object.entries(counts).map(([field, count]) => ({ field, count }))
    .sort((a, b) => b.count - a.count)
  return { recent: rows, byField }
}

// ─── AI decisions (the supervisor's questions & gaps) ─────────────────────────
// Written by the server's background analyst (/api/monitor/analyze, service-role).
// Here we only READ them and write the admin's ANSWER — same anon-direct pattern as
// monitor_findings. Answering a decision turns it into a remembered judgment the
// producers will consult (Phase 3). status: open | answered | dismissed.
export async function getDecisions(status = 'open') {
  if (!USE_SUPABASE) return []
  let q = supabase.from('ai_decisions').select('*')
  if (status !== 'all') q = q.eq('status', status)
  const { data, error } = await q.order('raised_at', { ascending: false })
  if (error) throw error
  return data || []
}

// answer = free-text note (analyst diagnostics); answerValue = chosen normal_key for a
// structured mapping decision (the producer auto-applies it). Pass whichever applies.
export async function answerDecision(signature, { answer = null, answerValue, status } = {}) {
  if (!USE_SUPABASE) return
  const nextStatus = status || ((answer || answerValue) ? 'answered' : 'open')
  const patch = { status: nextStatus, answered_at: new Date().toISOString() }
  if (answer !== undefined)      patch.answer = answer
  if (answerValue !== undefined) patch.answer_value = answerValue
  const { error } = await supabase
    .from('ai_decisions').update(patch).eq('signature', signature)
  if (error) throw error
}

// ─── Activity log (admin audit trail) ─────────────────────────────────────────
// Every resolved decision (answered or dismissed) is a timestamped record of what
// was decided. Powers the AI → Activity log sub-tab so an admin can review what was
// done — and re-open anything that turns out to be wrong. Newest first.
export async function getResolvedDecisions(limit = 400) {
  if (!USE_SUPABASE) return []
  const { data, error } = await supabase
    .from('ai_decisions').select('*')
    .neq('status', 'open')
    .order('answered_at', { ascending: false, nullsFirst: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

// Undo a past resolution: send the decision back to the open queue (clears the answer).
export async function reopenDecision(signature) {
  if (!USE_SUPABASE) return
  const { error } = await supabase
    .from('ai_decisions')
    .update({ status: 'open', answer: null, answer_value: null, answered_at: null })
    .eq('signature', signature)
  if (error) throw error
}

// ─── AI control plane (Admin → AI tab) ────────────────────────────────────────
// The AI tab is login-gated, so it runs as the `authenticated` role, which has full
// access to admin_config / ai_decisions / ai_cache (rls-policies.sql). That's why
// settings, rules and usage are all managed directly here — no server endpoint.
// The server READS ai_settings at request time (server.js effectiveAiSettings) with a
// per-key code fallback, so an unset key keeps today's behaviour. See ai-settings.sql.

// Effective AI-fillable field list + the current effective settings, straight from the
// server (single source of truth = property_model.js). The field list can't live in the
// SPA — it must come from the model. Settings here are the EFFECTIVE values (DB ?? default)
// for display; saveAiSettings writes the raw override blob.
export async function getAiConfigMeta() {
  const r = await fetch(`${SNTV_API}/api/admin/config?fresh=1`)
  if (!r.ok) throw new Error(`Failed to load AI config (HTTP ${r.status})`)
  const j = await r.json()
  return { aiFillFields: j.aiFillFields || [], aiSettings: j.aiSettings || {} }
}

// Raw override blob saved on the admin_config row (only the keys the admin changed).
export async function getAiSettings() {
  if (!USE_SUPABASE) return {}
  const { data, error } = await supabase
    .from('admin_config').select('ai_settings').eq('id', 1).maybeSingle()
  if (error) throw error
  return data?.ai_settings || {}
}

// Writes are a MERGE, not a replace: read the current blob and spread the patch over it.
// TWO sections of the AI tab now write ai_settings — the model-settings form AND the
// street re-check toggle in the "Force street re-verification" card. A blind overwrite
// here would let whichever section saved last wipe the keys owned by the other. Shallow
// merge is correct: every ai_settings value is a scalar or a whole-array set, never a
// nested object we'd need to deep-merge. Pass only the keys you mean to change.
export async function saveAiSettings(patch) {
  if (!USE_SUPABASE) throw new Error('Supabase connection required to save AI settings')
  const current = await getAiSettings()
  const merged  = { ...current, ...patch }
  // Bump version too so the server's 5-min admin_config cache (and the extension) refetch.
  const version = new Date().toISOString().replace(/[:.]/g, '-')
  const { error } = await supabase
    .from('admin_config').update({ ai_settings: merged, version }).eq('id', 1)
  if (error) throw error
  return version
}

// ─── AI usage / cost (ai_usage ledger) ────────────────────────────────────────
// Reads the ai_usage LEDGER (one row per real Gemini call, ALL consumers) — NOT ai_cache,
// which only logged the description pipeline and under-reported total spend ~50× (the street
// matcher was invisible). Server writes ai_usage via logAiUsage(); table = sql/ai_usage.sql.
// Aggregated client-side, split by consumer + model, over a date window. cost is derived from
// tokens when the row didn't store one (street_match doesn't price); failed calls cost $0 but
// count toward the error rate. Pricing mirrors ai_client.js (USD per 1M tokens [in, out]).
const AI_PRICE = {
  'gemini-2.5-flash':      [0.30, 2.50],
  'gemini-2.5-flash-lite': [0.10, 0.40],
  'gemini-2.0-flash':      [0.10, 0.40],
  'gemini-2.0-flash-lite': [0.075, 0.30],
  'gemini-1.5-flash':      [0.075, 0.30],
  'gemini-1.5-flash-8b':   [0.0375, 0.15],
  'gemini-1.5-pro':        [1.25, 5.00],
}
function _derivePrice(model, tin, tout, stored) {
  if (stored != null) return Number(stored) || 0
  const p = AI_PRICE[model] || AI_PRICE['gemini-2.5-flash-lite']
  return (tin / 1e6) * p[0] + (tout / 1e6) * p[1]
}
// Window can be given as `hours` (e.g. 1, 24) or `days` (e.g. 7, 30). `hours` wins
// when both are passed. windowLabel is returned for the UI so it stays in sync with
// the loaded data rather than the in-flight toggle selection.
export async function getAiUsage({ days, hours, limit = 20000 } = {}) {
  if (!USE_SUPABASE) return null
  const winHours = hours != null ? hours : (days != null ? days * 24 : 168)
  const windowLabel = winHours < 1 ? `${Math.round(winHours * 60)}m`
    : winHours <= 24 ? `${winHours}h` : (winHours % 24 === 0 ? `${winHours / 24}d` : `${winHours}h`)
  const since = new Date(Date.now() - winHours * 3600000).toISOString()
  // PostgREST caps EVERY response at 1000 rows; .limit(20000) does NOT override that
  // server cap, so the old single-shot read silently saw only the latest 1000 ai_usage
  // rows — the dashboard's call count froze at ~1000 and the error rate / cost were
  // computed over a truncated slice (2026-06-30). Page through with fetchAllRows so the
  // whole window is counted. `id` is the stable tiebreaker for clean page boundaries.
  // tokens_image / images (vision spend split, 2026-07-30) may not exist until
  // ai-usage-vision-columns.sql has been run — fall back to the old select so the
  // panel never goes blank on a missing migration.
  const mkQuery = sel => () =>
    supabase.from('ai_usage')
      .select(sel)
      .gte('created_at', since)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
  let rows
  try {
    rows = await fetchAllRows(mkQuery('consumer, model, tokens_in, tokens_out, tokens_image, images, cost, ok, created_at, id'))
  } catch {
    rows = await fetchAllRows(mkQuery('consumer, model, tokens_in, tokens_out, cost, ok, created_at, id'))
  }
  const byConsumer = {}, byModel = {}
  let totalCost = 0, tokensIn = 0, tokensOut = 0, calls = 0, errors = 0
  // Vision rollup. NOTE: tokens_image is the IMAGE share OF tokens_in (not additive), so
  // its cost is a slice of the already-counted input cost — priced at the model's input rate.
  const vision = { calls: 0, images: 0, tokensImage: 0, cost: 0, unmeasuredImages: 0 }
  for (const r of rows) {
    const tin = Number(r.tokens_in) || 0, tout = Number(r.tokens_out) || 0
    const failed = r.ok === false
    // A failed row costs what it RECORDED: since 2026-10-16 a Gemini answer that came back but failed
    // to parse is logged ok:false WITH its billed tokens/cost. A failure with no cost and no tokens
    // (HTTP error, network) is still $0 — Google bills nothing for those.
    const c = failed && r.cost == null && !tin && !tout ? 0 : _derivePrice(r.model, tin, tout, r.cost)
    calls++; if (failed) errors++
    totalCost += c; tokensIn += tin; tokensOut += tout
    const timg = Number(r.tokens_image) || 0, imgs = Number(r.images) || 0
    if (!failed && (timg > 0 || imgs > 0)) {
      vision.calls++; vision.images += imgs; vision.tokensImage += timg
      vision.cost += (timg / 1e6) * (AI_PRICE[r.model] || AI_PRICE['gemini-2.5-flash-lite'])[0]
      // ⚠️ Photos sent but no image-token split recorded (Lynceus/eval rows before 2026-10-16, local
      // models): their share is UNMEASURED, not free — the panel showed "64 images · $0".
      if (imgs > 0 && !timg) vision.unmeasuredImages += imgs
    }
    const ck = r.consumer || 'unknown'
    byConsumer[ck] = byConsumer[ck] || { consumer: ck, calls: 0, errors: 0, cost: 0, tokensIn: 0, tokensOut: 0, imageCost: 0, images: 0 }
    byConsumer[ck].calls++; if (failed) byConsumer[ck].errors++
    byConsumer[ck].cost += c; byConsumer[ck].tokensIn += tin; byConsumer[ck].tokensOut += tout
    if (!failed && timg > 0) byConsumer[ck].imageCost += (timg / 1e6) * (AI_PRICE[r.model] || AI_PRICE['gemini-2.5-flash-lite'])[0]
    if (!failed) byConsumer[ck].images += imgs
    const mk = r.model || 'unknown'
    byModel[mk] = byModel[mk] || { model: mk, calls: 0, cost: 0 }
    byModel[mk].calls++; byModel[mk].cost += c
  }
  return {
    windowHours: winHours, windowLabel, calls, errors, totalCost, tokensIn, tokensOut, capped: false,
    vision,
    byConsumer: Object.values(byConsumer).sort((a, b) => b.cost - a.cost),
    byModel: Object.values(byModel).sort((a, b) => b.cost - a.cost),
  }
}

// ─── Interpretation rules (ai_decisions rule:* answered) ──────────────────────
// A rule is free text injected verbatim into the AI prompt (server getPromptRules).
// Adding/removing one re-analyses cached listings (rulesHash is in the AI cache key).
export async function getAiRules() {
  if (!USE_SUPABASE) return []
  const { data, error } = await supabase
    .from('ai_decisions').select('*')
    .like('signature', 'rule:%').eq('status', 'answered')
    .order('raised_at', { ascending: false })
  if (error) throw error
  return data || []
}

export async function addAiRule(text, category = 'interpretation', appliesTo = null) {
  if (!USE_SUPABASE) throw new Error('Supabase connection required to add a rule')
  const body = (text || '').trim()
  if (!body) throw new Error('Rule text is empty')
  const now = new Date().toISOString()
  const signature = `rule:${crypto.randomUUID()}`
  const row = {
    signature, kind: 'recommendation',
    title: body.slice(0, 120), answer: body,
    rule_category: category,
    status: 'answered', raised_at: now, answered_at: now,
  }
  if (appliesTo) row.field = appliesTo
  const { error } = await supabase.from('ai_decisions').insert(row)
  if (error) throw error
  return signature
}

export async function deleteAiRule(signature) {
  if (!USE_SUPABASE) return
  const { error } = await supabase.from('ai_decisions').delete().eq('signature', signature)
  if (error) throw error
}

// Plain-language extraction rules derived from description-cleaner.js pattern groups (A-U).
// Seeded once via "Import from cleaner" button in Admin → AI → Rules.
// The admin can edit / delete / add more after import.
const CLEANER_EXTRACTION_RULES = [
  'Ignore all real estate agency and broker mentions (სААGENTO/სААGENТО, broker/ბROKERI, realtor/REALТORI, intermediary/SHUAMAVALI, სამაklero, etc.)',
  'Ignore owner self-identification claims ("I am the owner", "this is my apartment", მESAKUТРЕ/МЕPАТРОНЕ/მEPATRONE, ვAR MESАKУТРE, etc.)',
  'Ignore all call-to-action and contact invitation phrases (call me/DAMIREKET, contact me/DAMIKAVSHIRDIT, message me, reach out, SHEMAXMIANЕТ, etc.)',
  'Ignore copyright warnings and content-sharing restrictions (do not copy/DAAKOPIRОТ, without permission/NEBARTVIS GARESHE, unauthorized use, etc.)',
  'Ignore all price amounts with currency symbols or words (₾ $ € ლARI, DOLARI, EURO, USD, GEL, EUR and bare amounts like "500ლ")',
  'Ignore commission and fee phrases (SAKOMISiо/commission, without percentage/PROTSENTIS GARESHE, etc.)',
  'Ignore phone numbers in any format (Georgian +995 5xx xxx xxx, or any long digit sequences that look like phone numbers)',
  'Ignore email addresses (any text containing @)',
  'Ignore messenger app names and contact references (WhatsApp/VOTSAPI, Viber/VAIBERI, Telegram/TELEGRÄMI and their Georgian transliterations ვAIBERI, ვOTSAPI, ტELEGRAMI)',
  'Ignore street addresses (QUCHA/GAMZIRI + number, microdistrict/მIKRO numbers) and cadastral codes (S/K, SAKADASTRO)',
  'Ignore price negotiation phrases (final price/BOLO FASI, negotiate/FASTHZE SHEVTANXMDET, discount/FASDAKLEBA, deposit/DEPOZITI, advance payment/TSІNASTSARI)',
  'Ignore exclusive agency listing phrases (EKSKLUZIVI, exclusive, etc.)',
  'Ignore cooperation and collaboration phrases with agencies (VTHANAMSHROMLOVT, working together with agencies, etc.)',
  'Ignore discriminatory statements restricting by nationality or ethnicity (ინDOELEBZE AR, PAKISTANELEBZE AR, etc.)',
  'Ignore sentences written in Romanized Georgian (Georgian property words spelled with Latin characters, e.g. "iyideba bina", "damireket nomerze", "sartuli")',
  'Ignore Russian-language contact, owner, and price phrases (ZVONITYE/звоните, SOBSTVENNIK/собственник, TSENA/цена, TORG/торг, BEZ AGENTOV/без агентов, etc.)',
  'Ignore English-language contact, owner, and price phrases (call me, I am the owner, negotiable, price drop, no agents, direct from owner, etc.)',
  'Ignore bare name fragments left after contact stripping (e.g. "— ნUNU.", "— GIORGI.", lone dash + short word lines)',
  'Extract all genuine property facts: room counts, bedroom counts, floor number (aptFloor), total building floors (totalFloors), area in m², renovation/condition status, built-in features (fitted kitchen, wardrobes, appliances), amenities (elevator, parking, storage, balcony, terrace), building type, view characteristics, and any other objective property descriptor',
]

// ─── Street matching guide (ai_decisions rule:* rule_category='street_match') ──
// Rules written by admin, injected verbatim into every aiMatchStreet() prompt.
// Reuses addAiRule / deleteAiRule — only the category differs.
export async function getStreetMatchRules() {
  if (!USE_SUPABASE) return []
  const { data, error } = await supabase
    .from('ai_decisions').select('*')
    .like('signature', 'rule:%').eq('rule_category', 'street_match').eq('status', 'answered')
    .order('raised_at', { ascending: false })
  if (error) throw error
  return data || []
}

// ─── Street recheck epoch (admin_config.street_recheck_epoch) ─────────────────
// Bumping forces all place_aliases to be re-verified by AI on the next scrape.
export async function getStreetRecheckEpoch() {
  if (!USE_SUPABASE) return 1
  const { data, error } = await supabase
    .from('admin_config').select('street_recheck_epoch').eq('id', 1).maybeSingle()
  if (error) throw error
  return data?.street_recheck_epoch ?? 1
}

export async function bumpStreetRecheckEpoch() {
  if (!USE_SUPABASE) throw new Error('Supabase required')
  const current = await getStreetRecheckEpoch()
  const next = (current || 0) + 1
  const { error } = await supabase
    .from('admin_config').update({ street_recheck_epoch: next }).eq('id', 1)
  if (error) throw error
  return next
}

// ─── Street divergence observations ───────────────────────────────────────────
// Rows where an agent chose a different street than AI resolved (diverged=true).
export async function getStreetObservations({ limit = 50 } = {}) {
  if (!USE_SUPABASE) return []
  const { data, error } = await supabase
    .from('street_match_observations').select('*')
    .eq('diverged', true).order('created_at', { ascending: false }).limit(limit)
  if (error) throw error
  return data || []
}

// Full AI street-match audit trail (street_match_log) — one row per aiMatchStreet call
// (every retry attempt), with the exact candidates sent and picks returned. Newest first.
// Added 2026-07-01 so "how is the AI actually matching" is self-serve in the UI, not a
// DB query. This log grows FAST (several rows per match, many matches per scrape), so the
// viewer bounds it two ways: a time WINDOW (created_at >= now-hours) and a hard row cap.
// The window is the primary growth control; the cap is a backstop so a very busy window
// can't blow up the payload (the UI warns + tells you to narrow the window when the cap is
// hit). See StreetMatchLogView.
export async function getStreetMatchLog({ hours = 24, limit = 1000 } = {}) {
  if (!USE_SUPABASE) return []
  let q = supabase.from('street_match_log').select('*').order('created_at', { ascending: false }).limit(limit)
  if (hours) q = q.gte('created_at', new Date(Date.now() - hours * 3600000).toISOString())
  const { data, error } = await q
  if (error) throw error
  return data || []
}

// Resolve source-alias display info for a batch of alias ids (the log stores alias_id, not
// the source street name). Returns { [id]: { platform, external_id, name } }.
export async function aliasNamesByIds(ids = []) {
  if (!USE_SUPABASE || !ids.length) return {}
  const { data, error } = await supabase
    .from('place_aliases').select('id, platform, external_id, name').in('id', ids)
  if (error) throw error
  const map = {}
  for (const a of (data || [])) map[a.id] = a
  return map
}


export async function importCleanerRules() {
  if (!USE_SUPABASE) throw new Error('Supabase required')
  const { data: existing } = await supabase
    .from('ai_decisions').select('signature').like('signature', 'rule:%')
    .eq('rule_category', 'extraction').limit(1)
  if (existing && existing.length > 0)
    throw new Error('Extraction rules already exist — remove them first to re-import')
  const now = new Date().toISOString()
  const rows = CLEANER_EXTRACTION_RULES.map(text => ({
    signature: `rule:${crypto.randomUUID()}`,
    kind: 'recommendation',
    title: text.slice(0, 120),
    answer: text,
    rule_category: 'extraction',
    status: 'answered',
    raised_at: now,
    answered_at: now,
  }))
  const { error } = await supabase.from('ai_decisions').insert(rows)
  if (error) throw error
  return rows.length
}

// ─── Deals / Viewings pipeline (Results pillar — richer than the flat `deals`) ─
// One record = one property + the agent(s) handling it; visits/updates accumulate as
// viewing_events. Property + commission are snapshotted from a senTavus draft (project B,
// via the picker). Agents are a join table (equal commission split). See sql/viewings.sql.
const LS_VIEWINGS  = 'sva_viewings_v1'
const LS_V_AGENTS  = 'sva_viewing_agents_v1'
const LS_V_EVENTS  = 'sva_viewing_events_v1'

// Assemble a viewing with its agent_ids[] + agent_links[] (with commission_pct
// snapshot) + events[] (newest first).
function _assembleViewing(v, agentRows, eventRows) {
  const mine = agentRows.filter(a => a.viewing_id === v.id)
  return {
    ...v,
    agent_ids:   mine.map(a => a.agent_id),
    agent_links: mine.map(a => ({ agent_id: a.agent_id, commission_pct: a.commission_pct ?? null })),
    events:    eventRows.filter(e => e.viewing_id === v.id)
                        .sort((a, b) => (b.event_at || b.event_date || '').localeCompare(a.event_at || a.event_date || '')),
  }
}

export async function getViewings({ status, dateFrom, dateTo } = {}) {
  if (!USE_SUPABASE) {
    let rows = lsRead(LS_VIEWINGS)
    if (status)   rows = rows.filter(r => r.status === status)
    if (dateFrom) rows = rows.filter(r => (r.created_at || '').slice(0, 10) >= dateFrom)
    if (dateTo)   rows = rows.filter(r => (r.created_at || '').slice(0, 10) <= dateTo)
    const agents = lsRead(LS_V_AGENTS), events = lsRead(LS_V_EVENTS)
    return rows.map(v => _assembleViewing(v, agents, events))
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
  }
  // All three reads are paginated: StatsTab calls getViewings() with no date filter,
  // so views grows unbounded, and viewing_events (several rows per viewing) can exceed
  // 1000 well before views does — truncating it would drop close events and corrupt
  // the commission/close-date math. Each ordered by a unique key for clean paging.
  const views = await fetchAllRows(() => {
    let q = supabase.from('viewings').select('*')
    if (status)   q = q.eq('status', status)
    if (dateFrom) q = q.gte('created_at', new Date(`${dateFrom}T00:00:00`).toISOString())
    if (dateTo)   q = q.lte('created_at', new Date(`${dateTo}T23:59:59.999`).toISOString())
    return q.order('created_at', { ascending: false }).order('id', { ascending: true })
  })
  const ids = views.map(v => v.id)
  if (!ids.length) return []
  const [agents, events] = await Promise.all([
    fetchAllRows(() => supabase.from('viewing_agents').select('*').in('viewing_id', ids)
      .order('viewing_id', { ascending: true }).order('agent_id', { ascending: true })),
    fetchAllRows(() => supabase.from('viewing_events').select('*').in('viewing_id', ids)
      .order('id', { ascending: true })),
  ])
  return views.map(v => _assembleViewing(v, agents || [], events || []))
}

// getViewingsBySourceUrls was DELETED 2026-07-20 (BACKLOG B76). It was already dead —
// zero callers — superseded by the server-projected GET /api/core/property-viewings
// (getPropertyViewings in db/core.js). Removed rather than left lying around because
// re-adopting it would silently reintroduce TWO fixed bugs: it read the scheduler
// viewings table CLIENT-SIDE (shipping every client's name/phone to any agent — the
// leak Track 2 closed), and it interpolated full ad URLs into one unchunked or=()
// (the 8KB request-line wall, B70).

// record = viewing columns; agentIds = uuid[]. commByAgent = { agentId: pct } snapshots
// each agent's commission % at the time (only when closing). Optional firstEvent seeds the timeline.
export async function addViewing(record, agentIds = [], firstEvent = null, commByAgent = null) {
  if (!USE_SUPABASE) {
    const row = { ...record, id: crypto.randomUUID(), created_at: new Date().toISOString(), updated_at: new Date().toISOString() }
    lsWrite(LS_VIEWINGS, [row, ...lsRead(LS_VIEWINGS)])
    lsWrite(LS_V_AGENTS, [...lsRead(LS_V_AGENTS), ...agentIds.map(agent_id => ({ viewing_id: row.id, agent_id, commission_pct: commByAgent?.[agent_id] ?? null }))])
    if (firstEvent) await addViewingEvent(row.id, firstEvent)
    return row
  }
  const { data: row, error } = await supabase.from('viewings').insert(record).select().single()
  if (error) throw error
  await _replaceViewingAgents(row.id, agentIds, commByAgent)
  if (firstEvent) await addViewingEvent(row.id, firstEvent)
  return row
}

export async function updateViewing(id, updates, agentIds = null, commByAgent = null) {
  if (!USE_SUPABASE) {
    const rows = lsRead(LS_VIEWINGS).map(r => r.id === id ? { ...r, ...updates, updated_at: new Date().toISOString() } : r)
    lsWrite(LS_VIEWINGS, rows)
    if (agentIds) {
      const prev = Object.fromEntries(lsRead(LS_V_AGENTS).filter(a => a.viewing_id === id).map(a => [a.agent_id, a.commission_pct]))
      const others = lsRead(LS_V_AGENTS).filter(a => a.viewing_id !== id)
      lsWrite(LS_V_AGENTS, [...others, ...agentIds.map(agent_id => ({ viewing_id: id, agent_id, commission_pct: commByAgent?.[agent_id] ?? prev[agent_id] ?? null }))])
    }
    return rows.find(r => r.id === id)
  }
  const { data, error } = await supabase.from('viewings').update(updates).eq('id', id).select().single()
  if (error) throw error
  if (agentIds) await _replaceViewingAgents(id, agentIds, commByAgent)
  return data
}

// Lock each agent's current commission % onto a viewing — called when a deal is
// closed, so income reflects the rate the agent had at close.
// managerLinks: [{ agent_id, commission_pct }] — the office managers owed a team override
// on this deal, resolved by the caller (see managerLinksForDeal in lib/performance.js) and
// FROZEN here for the same reason commission_pct is frozen: agents.commission_manager is a
// live number, so resolving it at read time would let one edit rewrite every past payout.
// Written even when empty ([]), so "no managers owed" is a recorded fact and not a null that
// a later reader has to guess about.
export async function snapshotViewingCommissions(viewingId, commByAgent = {}, managerLinks = null) {
  if (!USE_SUPABASE) {
    lsWrite(LS_V_AGENTS, lsRead(LS_V_AGENTS).map(a =>
      a.viewing_id === viewingId && commByAgent[a.agent_id] != null ? { ...a, commission_pct: commByAgent[a.agent_id] } : a))
    if (managerLinks) lsWrite(LS_VIEWINGS, lsRead(LS_VIEWINGS).map(v =>
      v.id === viewingId ? { ...v, manager_links: managerLinks } : v))
    return
  }
  const { data } = await supabase.from('viewing_agents').select('agent_id').eq('viewing_id', viewingId)
  for (const r of data || []) {
    if (commByAgent[r.agent_id] == null) continue
    await supabase.from('viewing_agents').update({ commission_pct: commByAgent[r.agent_id] })
      .eq('viewing_id', viewingId).eq('agent_id', r.agent_id)
  }
  if (managerLinks) {
    await supabase.from('viewings').update({ manager_links: managerLinks }).eq('id', viewingId)
  }
}

export async function deleteViewing(id) {
  if (!USE_SUPABASE) {
    lsWrite(LS_VIEWINGS, lsRead(LS_VIEWINGS).filter(r => r.id !== id))
    lsWrite(LS_V_AGENTS, lsRead(LS_V_AGENTS).filter(a => a.viewing_id !== id))
    lsWrite(LS_V_EVENTS, lsRead(LS_V_EVENTS).filter(e => e.viewing_id !== id))
    return
  }
  const { error } = await supabase.from('viewings').delete().eq('id', id)   // cascades agents + events
  if (error) throw error
}

// Append a timeline event; if it carries new_status, advance the viewing's status too.
export async function addViewingEvent(viewingId, event) {
  const row = { viewing_id: viewingId, event_date: event.event_date || new Date().toISOString().slice(0, 10), ...event }
  let saved = { ...row, id: crypto.randomUUID(), created_at: new Date().toISOString() }
  if (!USE_SUPABASE) {
    lsWrite(LS_V_EVENTS, [...lsRead(LS_V_EVENTS), saved])
  } else {
    const { data, error } = await supabase.from('viewing_events').insert(row).select().single()
    if (error) throw error
    saved = data
  }
  if (event.new_status) await updateViewing(viewingId, { status: event.new_status })
  return saved   // includes the real DB id (needed to undo)
}

// Undo helper: remove a single event (used by the 15s revert on outcome actions).
export async function deleteViewingEvent(eventId) {
  if (!USE_SUPABASE) {
    lsWrite(LS_V_EVENTS, lsRead(LS_V_EVENTS).filter(e => e.id !== eventId))
    return
  }
  const { error } = await supabase.from('viewing_events').delete().eq('id', eventId)
  if (error) throw error
}

async function _replaceViewingAgents(viewingId, agentIds, commByAgent = null) {
  // Preserve any existing commission_pct snapshot for agents that remain, and
  // apply commByAgent when provided (e.g. closing). Replacing rows otherwise.
  const { data: existing } = await supabase.from('viewing_agents').select('agent_id, commission_pct').eq('viewing_id', viewingId)
  const prevPct = Object.fromEntries((existing || []).map(r => [r.agent_id, r.commission_pct]))
  await supabase.from('viewing_agents').delete().eq('viewing_id', viewingId)
  if (agentIds.length) {
    const rows = agentIds.map(agent_id => {
      const pct = (commByAgent && commByAgent[agent_id] != null) ? commByAgent[agent_id] : prevPct[agent_id]
      return { viewing_id: viewingId, agent_id, ...(pct != null ? { commission_pct: pct } : {}) }
    })
    const { error } = await supabase.from('viewing_agents').insert(rows)
    if (error) throw error
  }
}

// ─── Draft picker (project B, via the api.sentavus.com search endpoint) ───────
// Resolves an id / short code / source url / cadastral / platform link to candidate drafts
// (property + commission + owner agent email + contacts). Guarded by the shared scheduler
// session token (x-viewer-token). Returns [] in local mode or when not signed in.
//
// 🚨 B53 step 2c (2026-07-26) — THE BROWSER NO LONGER HOLDS ANY SHARED SECRET.
// VITE_* vars are INLINED INTO THE PUBLIC BUNDLE at build time, so the old
// VITE_SCHEDULER_SECRET was readable by anyone who opened the JS (confirmed: the built
// main-*.js contained the literal key) — it authenticated nobody while granting everybody.
// Never reintroduce a VITE_-prefixed credential here; the agent's own verified session is
// the only thing that may authenticate a browser call.
// Server side: server.js `schedKeyGuard` + the /api/scheduler/* mgmt middleware.
// Same cached+deadlined read as every other call (see getAccessToken above) — never
// getSession() per request.
const schedToken = getAccessToken

async function schedHeaders(json = false) {
  const token = await schedToken()
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { 'x-viewer-token': token } : {}),
  }
}

export async function searchDrafts(q) {
  const query = (q || '').trim()
  if (query.length < 3 || !(await schedToken())) return []
  const r = await fetch(`${SNTV_API}/api/drafts/search?q=${encodeURIComponent(query)}`, {
    headers: await schedHeaders(),
  })
  if (!r.ok) throw new Error(`Draft search failed (HTTP ${r.status})`)
  const { results } = await r.json()
  return results || []
}

// ─── Sentavus (property DB) agent sync ────────────────────────────────────────
// Creating the auth user and bulk-unassigning drafts both need service-role on the
// property project (drafts RLS is block_all; auth-user creation needs the Admin API),
// so they go through api.sentavus.com — never the anon key from this public SPA.
// The property login is `${login_id}@sentavus.com`.

// Create the matching agent account in the property DB when one is added here.
export async function createSentavusUser(loginId, password) {
  if (!(await schedToken())) throw new Error('Not signed in')
  const r = await fetch(`${SNTV_API}/api/scheduler/create-agent`, {
    method: 'POST',
    headers: await schedHeaders(true),
    body: JSON.stringify({ login_id: loginId, password }),
  })
  const data = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(data.error || `create-agent failed (HTTP ${r.status})`)
  return data   // { ok, user_id, existed?, sched_user_id? } — sched_user_id is the SCHEDULER-project auth uid
}

// Propagate a login/password change to the matching property account (and, since
// 2026-07-05, the scheduler-project login too — same pair, both identities).
// schedAuthUserId: the agents.auth_user_id already linked, so the server updates
// the exact scheduler auth user instead of resolving by (possibly renamed) email.
export async function updateSentavusUser({ oldLoginId, loginId, password, schedAuthUserId }) {
  if (!(await schedToken())) throw new Error('Not signed in')
  const r = await fetch(`${SNTV_API}/api/scheduler/update-agent`, {
    method: 'POST',
    headers: await schedHeaders(true),
    body: JSON.stringify({ old_login_id: oldLoginId || '', login_id: loginId, password: password || '', sched_auth_user_id: schedAuthUserId || '' }),
  })
  const data = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(data.error || `update-agent failed (HTTP ${r.status})`)
  return data
}

// Change the SIGNED-IN agent's own password. Separate from updateSentavusUser on
// purpose: that one targets /api/scheduler/update-agent, which is gated to mgmt roles,
// so an ordinary agent calling it got a 401 that useSchedule.js swallowed — the dialog
// said "changed" while neither core nor scheduler auth ever saw the new password
// (2026-08-01). This route resolves the account from the VERIFIED token alone, which is
// why it takes no login id: it cannot address anyone but the caller.
// Throws on failure by design — a credential change that silently no-ops is the bug.
export async function updateMyPassword(password) {
  if (!(await schedToken())) throw new Error('Not signed in')
  const r = await fetch(`${SNTV_API}/api/scheduler/my-credentials`, {
    method: 'POST',
    headers: await schedHeaders(true),
    body: JSON.stringify({ password }),
  })
  const data = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(data.error || `password change failed (HTTP ${r.status})`)
  return data
}

// Lock/unlock an agent's login on BOTH auth systems (scheduler + extension) — used
// by Mark-as-left (lock) and reactivation (unlock). Non-fatal by design at the call
// site: a resigned agent losing account access matters more than a failed toast.
export async function lockAgentLogin(loginId, locked) {
  if (!loginId || !(await schedToken())) return null
  const r = await fetch(`${SNTV_API}/api/scheduler/lock-agent`, {
    method: 'POST',
    headers: await schedHeaders(true),
    body: JSON.stringify({ login_id: loginId, locked }),
  })
  const data = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(data.error || `lock-agent failed (HTTP ${r.status})`)
  return data
}

// How many property cards an agent currently owns (for the Mark-as-left dialog).
export async function countAgentDrafts(loginId) {
  if (!loginId || !(await schedToken())) return null
  const r = await fetch(`${SNTV_API}/api/scheduler/agent-cards?login_id=${encodeURIComponent(loginId)}`, {
    headers: await schedHeaders(),
  })
  if (!r.ok) throw new Error(`agent-cards failed (HTTP ${r.status})`)
  const { count } = await r.json()
  return count
}

// ─── Agent visibility (extension "check" privacy) ─────────────────────────────
// Per-agent public/private state for the extension's duplicate-check results.
// Data lives in agent_visibility (THIS Supabase project) but reads/writes go through
// api.sentavus.com (x-scheduler-key) — the table has RLS with no anon policies, and
// the server owns the delayed-activation + force/hidden rules. Admin changes apply
// INSTANTLY (no 24h delay, unlike the agent's own toggle in the extension popup).

// Full roster with effective visibility state → [{ email, isPublic, pendingTarget, effectiveAt, adminForced, forcedHidden }]
export async function getAgentVisibilityList() {
  if (!(await schedToken())) return []
  const r = await fetch(`${SNTV_API}/api/scheduler/agent-visibility`, {
    headers: await schedHeaders(),
  })
  if (!r.ok) throw new Error(`visibility list failed (HTTP ${r.status})`)
  const { agents } = await r.json()
  return agents || []
}

// Set one agent instantly. isPublic/forced/hidden are each optional (undefined = leave as-is).
// forced:false always clears hidden server-side (can't hide a lock that isn't there).
export async function setAgentVisibility({ email, isPublic, forced, hidden }) {
  if (!(await schedToken())) throw new Error('Not signed in')
  const r = await fetch(`${SNTV_API}/api/scheduler/agent-visibility`, {
    method: 'POST',
    headers: await schedHeaders(true),
    body: JSON.stringify({ email, isPublic, forced, hidden }),
  })
  const data = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(data.error || `visibility update failed (HTTP ${r.status})`)
  return data
}

// Force the WHOLE roster at once (instant). forced defaults to true server-side.
export async function bulkAgentVisibility({ isPublic, forced, hidden }) {
  if (!(await schedToken())) throw new Error('Not signed in')
  const r = await fetch(`${SNTV_API}/api/scheduler/agent-visibility/bulk`, {
    method: 'POST',
    headers: await schedHeaders(true),
    body: JSON.stringify({ isPublic, forced, hidden }),
  })
  const data = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(data.error || `bulk visibility failed (HTTP ${r.status})`)
  return data   // { updated }
}

// Return all of a leaving agent's property cards to the pool (set drafts.user_id NULL).
export async function releaseAgentDrafts(loginId) {
  if (!(await schedToken())) throw new Error('Not signed in')
  const r = await fetch(`${SNTV_API}/api/scheduler/release-agent`, {
    method: 'POST',
    headers: await schedHeaders(true),
    body: JSON.stringify({ login_id: loginId }),
  })
  const data = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(data.error || `release-agent failed (HTTP ${r.status})`)
  return data   // { ok, released }
}

// ── THE AGENT'S OWN ATTENDANCE (owner, need-care list 2026-09-02) ────────────────────────
// "Agents come to the office and press I came… but selecting I came must only be allowed
// from this IP address, so they select I came actually mean they came."
//
// ⚠️ THESE THREE ARE RPCs, NOT TABLE WRITES, AND THAT IS THE WHOLE POINT. Every other
// attendance write in this app is a direct upsert from the browser, which means the client
// decides who and when. Here the database decides both: the agent comes from the SESSION
// (auth.uid() → agents.auth_user_id) and the location from the address Supabase's EDGE
// observed, so neither is a value this code could get wrong or a user could supply.
// See db/scheduler-migrations/0002 for what that does and does not guarantee — notably that
// `attendance` still has RLS disabled, so this is an enforced DOOR, not a sealed room.
export async function markOwnArrival() {
  if (!USE_SUPABASE) return { ok: false, reason: 'offline' }
  const { data, error } = await supabase.rpc('mark_own_arrival')
  if (error) throw error
  return data   // { ok, reason?, check_in?, already?, seen? }
}

// Leaving is allowed from anywhere — an agent's day ends at a viewing as often as at a desk.
export async function markOwnDeparture() {
  if (!USE_SUPABASE) return { ok: false, reason: 'offline' }
  const { data, error } = await supabase.rpc('mark_own_departure')
  if (error) throw error
  return data   // { ok, reason?, check_out?, already? }
}

// ASKED BEFORE THE BUTTON IS OFFERED, so an agent standing outside the office is told why
// rather than meeting a refusal on press — the standing "a lock must name an action they
// HAVE" rule. Returns { ok, reason, seen, office_enforces }; `office_enforces` false means
// this office has no addresses configured and the permission comes from the ABSENCE of a
// rule, which the UI says out loud instead of implying a check that did not happen.
export async function checkAttendanceLocation(agentId) {
  if (!USE_SUPABASE || !agentId) return null
  const { data, error } = await supabase.rpc('attendance_location_ok', { p_agent_id: agentId })
  if (error) throw error
  return Array.isArray(data) ? data[0] : data
}
